import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
    Plus, Edit3, Trash2, ExternalLink, ChevronDown, ChevronUp,
    Globe, CheckCircle, X, Save, Layers, Package,
    Eye, BookOpen, Info,
    FileText, BarChart2, Sparkles, Users,
    GripVertical, ScrollText, Zap, Brain, CreditCard, GraduationCap, Video,
} from 'lucide-react';
import {
    DndContext, closestCenter,
    KeyboardSensor, PointerSensor,
    useSensor, useSensors, DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove, SortableContext,
    sortableKeyboardCoordinates, verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    DescBlock, DESC_ICONS,
    parseDescBlocks, serializeDescBlocks,
} from '../../../../components/admin/products/tabs/CreateProducts';
import {
    defaultApostilaDemoData,
    ApostilaDemoData, ApostilaDemoSegment, ApostilaDemoQuizOption, ApostilaDemoNavItem,
} from '../../../components/product-blocks/ApostilaDemoBlock';
import {
    defaultFlashcardsDemoData,
    FlashcardsDemoData, FlashcardsDemoCard,
} from '../../../components/product-blocks/FlashcardsDemoBlock';
import {
    defaultMetodoDemoData,
    MetodoDemoData, MetodoDemoWorld,
} from '../../../components/product-blocks/MetodoDemoBlock';
import {
    defaultEstudoAtivoDemoData,
    EstudoAtivoDemoData, EstudoAtivoColumn,
} from '../../../components/product-blocks/EstudoAtivoDemoBlock';
import {
    defaultTutoriaDemoData,
    TutoriaDemoData, TutoriaCard,
} from '../../../components/product-blocks/TutoriaDemoBlock';
import {
    defaultVideoDemoData,
    VideoDemoData,
} from '../../../components/product-blocks/VideoDemoBlock';
import { DemoPillar, DemoClosing } from '../../../components/product-blocks/DemoBlockShared';
import { getRyanMedicalIcon } from '../../../constants/ryanMedicalIcons';
import { RYAN_HERO_FONTS } from '../../../constants/ryanHeroFonts';
import RyanSubdeckIconPicker from '../../../components/RyanSubdeckIconPicker';
import RyanAdminProductUpsell from './RyanAdminProductUpsell';
import RyanAdminCampaigns from './RyanAdminCampaigns';
import './RyanAdminProducts.css';

// ── Types ──────────────────────────────────────────────────────────────

interface RyanSpaceRef {
    _id: string;
    name: string;
    groupId: string | null;
    icon: string;
    colorId: string;
}

interface RyanSpaceGroupRef {
    _id: string;
    name: string;
    order: number;
    requiredInstitutionTag?: string | null;
}

interface ContentBlock {
    id: string;
    type: 'apostila_demo' | 'flashcards_demo' | 'metodo_demo' | 'estudo_ativo_demo' | 'tutoria_demo' | 'video_demo';
    enabled: boolean;
    order: number;
    backgroundVariant: 'white' | 'green' | 'light' | 'dark';
    data: ApostilaDemoData | FlashcardsDemoData | MetodoDemoData | EstudoAtivoDemoData | TutoriaDemoData | VideoDemoData;
}

interface OsceScenario {
    badge: string;
    examinerLabel: string;
    question: string;
    hint: string;
    answerLabel: string;
    answerText: string;
}

interface PublicPage {
    enabled: boolean;
    pixKey: string;
    pixHolderName: string;
    pixCity: string;
    whatsappNumber: string;
    // Preço pode aparecer no Hero, no bloco "Adquira agora", nos dois, ou em
    // nenhum — independentes (antes era um único showPrice pros dois).
    showPriceHero: boolean;
    showPriceClose: boolean;
    showStats: boolean;
    heroDescription: string;
    heroTitleFont: string;
    statsMode: 'auto' | 'custom';
    statsCustomContent: string;
    differentialEnabled: boolean;
    differentialTitle: string;
    differentialContent: string;
    differentialRightMode: 'none' | 'animation_osce' | 'stat_cards';
    differentialStatCards: Array<{ icon: string; value: string; label: string }>;
    osceScenario: OsceScenario;
    contentBlocks: ContentBlock[];
    gridCols: number;
    // 'tabs'/'categories'/'custom' nunca coexistem — só um por vez. 'flat'
    // ignora a estrutura interna do campo de estudo (só Apostilas/Flashcards).
    // 'custom' = seções livres definidas pelo admin (ver customSections
    // abaixo), único modo que agrupa itens de vários campos/abas sob um nome
    // escolhido à mão — resolve o caso de um campo inteiro sem aba única.
    contentOrganization: 'tabs' | 'categories' | 'flat' | 'custom';
    // Só importa quando contentOrganization é 'tabs' ou 'categories': cada
    // aba/categoria vira um bloco que abre/fecha (em vez de sempre expandido).
    contentDropdown: boolean;
    subdeckDescriptions: Record<string, string>;
    subdeckPreviewLinks: Record<string, string>;
    // Ordem escolhida via drag-and-drop (comparável só dentro do mesmo tipo:
    // apostila vs flashcards) — ver server/models/RyanProduct.js.
    subdeckOrder: Record<string, number>;
    // Ícone custom por aba (tabId -> nome do ícone médico) — só usado quando
    // contentOrganization é 'tabs'.
    tabIcons: Record<string, string>;
    // "Personalizado" — seções com nome/ícone livres (ver comentário no
    // schema, server/models/RyanProduct.js).
    customSections: Array<{ id: string; name: string; icon: string | null; description: string; order: number }>;
    // subdeckId -> customSections[].id. Sem entrada = cai no bucket "Outros
    // materiais" da página pública.
    customSectionAssignments: Record<string, string>;
    checkoutShowFlashcards: boolean;
    checkoutShowApostilas: boolean;
    checkoutShowDecks: boolean;
    // Contagem de flashcards/decks no bloco "Conteúdo incluído" — geral
    // (chips agregados) e individual (badge por card de subdeck/apostila).
    showContentGroupCounts: boolean;
    showContentItemCounts: boolean;
}

interface GrantRef {
    type: 'space' | 'tab' | 'subdeck';
    refId: string;
}

interface RyanProduct {
    _id: string;
    title: string;
    grants: GrantRef[];
    priceCents: number;
    periodo: number | null;
    validadeTexto: string;
    publicSlug: string | null;
    publicPage: PublicPage;
    active: boolean;
    // Data real de expiração do acesso (aplicada pelo webhook do Mercado
    // Pago a todo mundo que comprar este produto) — string ISO ou null.
    // Independente de validadeTexto (texto livre mostrado na página pública).
    accessExpiresAt: string | null;
    // Tags de instituição concedidas à conta na compra (ex: ['afya']) —
    // somadas a RyanAccess.institutionTags, nunca substitui. Controla só se
    // um RyanSpaceGroup com requiredInstitutionTag aparece na home do
    // comprador — não é acesso a conteúdo (isso continua sendo `grants`).
    grantsInstitutionTags: string[];
    createdAt: string;
    userCount?: number;
}

interface SubdeckItem {
    _id: string;
    name: string;
    type: string;
    iconId: string | null;
    spaceName: string;
    tabId: string | null;
}

interface SpaceContent {
    _id: string;
    name: string;
    subdecks: Array<{ _id: string; name: string; type: string; iconId: string | null; tabId: string | null }>;
    tabs: Array<{ _id: string; name: string }>;
}

// ── Defaults ───────────────────────────────────────────────────────────

const defaultPublicPage = (): PublicPage => ({
    enabled: false,
    pixKey: '',
    pixHolderName: '',
    pixCity: 'SAO PAULO',
    whatsappNumber: '',
    showPriceHero: true,
    showPriceClose: true,
    showStats: true,
    heroDescription: '',
    heroTitleFont: 'serif-classic',
    statsMode: 'auto',
    statsCustomContent: '',
    differentialEnabled: false,
    differentialTitle: 'Como funciona?',
    differentialContent: '',
    differentialRightMode: 'none',
    differentialStatCards: [
        { icon: 'BookOpen', value: '', label: '' },
        { icon: 'Stethoscope', value: '', label: '' },
        { icon: 'Zap', value: '', label: '' },
        { icon: 'Brain', value: '', label: '' },
    ],
    osceScenario: {
        badge: 'Estação OSCE · Cardiologia',
        examinerLabel: 'O examinador pergunta:',
        question: 'Descreva o sopro esperado: momento do ciclo cardíaco, caráter, foco de máxima intensidade e irradiação.',
        hint: 'Responda em voz alta, depois confira:',
        answerLabel: 'Descrição do sopro',
        answerText: 'Sopro sistólico ejetivo, crescendo-decrescendo (em diamante), rude e granular. Foco de máxima intensidade no 2° EIC direito (foco aórtico). Irradia para a fúrcula esternal e carótidas.',
    },
    contentBlocks: [],
    gridCols: 3,
    contentOrganization: 'tabs',
    contentDropdown: false,
    subdeckDescriptions: {},
    subdeckPreviewLinks: {},
    subdeckOrder: {},
    tabIcons: {},
    customSections: [],
    customSectionAssignments: {},
    checkoutShowFlashcards: false,
    checkoutShowApostilas: false,
    checkoutShowDecks: false,
    showContentGroupCounts: true,
    showContentItemCounts: true,
});

const tok = () => localStorage.getItem('accessToken') || '';

const genId = () => (
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `id_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
);

// ── Inline DescBlock Editor ──────────────────────────────────────────

const DescBlockEditorInline: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
    const [blocks, setBlocks] = useState<DescBlock[]>(() => parseDescBlocks(value));
    const [iconPicker, setIconPicker] = useState<number | null>(null);

    const push = (newBlocks: DescBlock[]) => {
        setBlocks(newBlocks);
        onChange(serializeDescBlocks(newBlocks));
    };

    const addLi = () => push([...blocks, { type: 'li', icon: 'CheckCircle', text: '' }]);
    const addP  = () => push([...blocks, { type: 'p', text: '' }]);
    const del   = (i: number) => push(blocks.filter((_, idx) => idx !== i));
    const setText = (i: number, text: string) => {
        const b = [...blocks]; b[i] = { ...b[i], text }; push(b);
    };
    const setIcon = (i: number, icon: string) => {
        const b = [...blocks]; b[i] = { ...b[i], icon }; push(b);
        setIconPicker(null);
    };

    return (
        <div className="rap-dbe">
            {blocks.map((block, i) => {
                const IC = block.type === 'li'
                    ? (DESC_ICONS.find(ic => ic.name === block.icon)?.component ?? CheckCircle)
                    : null;
                return (
                    <div key={i} className={`rap-dbe-row ${block.type === 'p' ? 'rap-dbe-row--p' : ''}`}>
                        {block.type === 'li' && (
                            <div className="rap-dbe-icon-wrap">
                                <button type="button" className="rap-dbe-icon-btn"
                                    onClick={() => setIconPicker(iconPicker === i ? null : i)}>
                                    {IC && <IC size={14} />}
                                </button>
                                {iconPicker === i && (
                                    <div className="rap-dbe-picker">
                                        {DESC_ICONS.map(ic => {
                                            const C = ic.component;
                                            return (
                                                <button key={ic.name} type="button"
                                                    className={`rap-dbe-pick ${block.icon === ic.name ? 'active' : ''}`}
                                                    title={ic.label}
                                                    onClick={() => setIcon(i, ic.name)}>
                                                    <C size={13} />
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                        <input
                            className="rap-dbe-input"
                            value={block.text}
                            onChange={e => setText(i, e.target.value)}
                            placeholder={block.type === 'li' ? 'Tópico...' : 'Parágrafo...'}
                        />
                        <button type="button" className="rap-dbe-del" onClick={() => del(i)}>
                            <X size={12} />
                        </button>
                    </div>
                );
            })}
            <div className="rap-dbe-actions">
                <button type="button" className="rap-dbe-add-li" onClick={addLi}>
                    <Plus size={11} /> Tópico
                </button>
                <button type="button" className="rap-dbe-add-p" onClick={addP}>
                    <Plus size={11} /> Parágrafo
                </button>
            </div>
        </div>
    );
};

// ── Blocos de Demonstração — sub-editores ───────────────────────────

const moveItem = <T,>(arr: T[], i: number, dir: -1 | 1): T[] => {
    const j = i + dir;
    if (j < 0 || j >= arr.length) return arr;
    const next = [...arr];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
};

const ApostilaSegmentsEditor: React.FC<{
    segments: ApostilaDemoSegment[];
    onChange: (segments: ApostilaDemoSegment[]) => void;
}> = ({ segments, onChange }) => {
    const update = (i: number, patch: Partial<ApostilaDemoSegment>) => {
        const next = [...segments]; next[i] = { ...next[i], ...patch }; onChange(next);
    };
    const add = () => onChange([...segments, { id: genId(), text: '', highlight: false, quiz: false, note: '' }]);
    const remove = (i: number) => onChange(segments.filter((_, idx) => idx !== i));

    return (
        <div className="rap-block-list">
            {segments.map((seg, i) => (
                <div key={seg.id} className="rap-block-subitem">
                    <div className="rap-block-subitem-order">
                        <button type="button" className="rap-order-btn" onClick={() => onChange(moveItem(segments, i, -1))} disabled={i === 0}><ChevronUp size={12} /></button>
                        <button type="button" className="rap-order-btn" onClick={() => onChange(moveItem(segments, i, 1))} disabled={i === segments.length - 1}><ChevronDown size={12} /></button>
                    </div>
                    <div className="rap-block-subitem-body">
                        <textarea className="rap-textarea" rows={2} value={seg.text}
                            onChange={e => update(i, { text: e.target.value })}
                            placeholder="Trecho do parágrafo..." />
                        <div className="rap-checks-row">
                            <label className="rap-check">
                                <input type="checkbox" checked={seg.highlight} onChange={e => update(i, { highlight: e.target.checked })} /> Grifar
                            </label>
                            <label className="rap-check">
                                <input type="checkbox" checked={seg.quiz} onChange={e => update(i, { quiz: e.target.checked })} /> Badge de quiz
                            </label>
                        </div>
                        <input className="rap-input" value={seg.note} onChange={e => update(i, { note: e.target.value })}
                            placeholder="Nota (opcional — vazio = sem nota)" />
                    </div>
                    <button type="button" className="rap-dbe-del" onClick={() => remove(i)}><X size={12} /></button>
                </div>
            ))}
            <button type="button" className="rap-dbe-add-li" onClick={add}><Plus size={11} /> Parte do texto</button>
        </div>
    );
};

const ApostilaQuizOptionsEditor: React.FC<{
    options: ApostilaDemoQuizOption[];
    onChange: (options: ApostilaDemoQuizOption[]) => void;
}> = ({ options, onChange }) => {
    const update = (i: number, patch: Partial<ApostilaDemoQuizOption>) => {
        const next = [...options]; next[i] = { ...next[i], ...patch }; onChange(next);
    };
    const setCorrect = (i: number) => onChange(options.map((o, idx) => ({ ...o, correct: idx === i })));
    const add = () => { if (options.length >= 4) return; onChange([...options, { id: genId(), text: '', correct: options.length === 0 }]); };
    const remove = (i: number) => onChange(options.filter((_, idx) => idx !== i));

    return (
        <div className="rap-block-list">
            {options.map((opt, i) => (
                <div key={opt.id} className="rap-quiz-opt-row">
                    <input type="radio" name="apt-quiz-correct" checked={opt.correct} onChange={() => setCorrect(i)} title="Marcar como correta" />
                    <input className="rap-input" value={opt.text} onChange={e => update(i, { text: e.target.value })}
                        placeholder={`Alternativa ${String.fromCharCode(65 + i)}`} />
                    <button type="button" className="rap-dbe-del" onClick={() => remove(i)} disabled={options.length <= 2}><X size={12} /></button>
                </div>
            ))}
            {options.length < 4 && (
                <button type="button" className="rap-dbe-add-li" onClick={add}><Plus size={11} /> Alternativa</button>
            )}
        </div>
    );
};

const ApostilaNavItemsEditor: React.FC<{
    items: ApostilaDemoNavItem[];
    onChange: (items: ApostilaDemoNavItem[]) => void;
}> = ({ items, onChange }) => {
    const update = (i: number, patch: Partial<ApostilaDemoNavItem>) => {
        const next = [...items]; next[i] = { ...next[i], ...patch }; onChange(next);
    };
    const setActive = (i: number) => onChange(items.map((it, idx) => ({ ...it, active: idx === i })));
    const add = () => onChange([...items, { id: genId(), title: '', progress: 0, hasQuiz: false, active: items.length === 0 }]);
    const remove = (i: number) => onChange(items.filter((_, idx) => idx !== i));

    return (
        <div className="rap-block-list">
            {items.map((it, i) => (
                <div key={it.id} className="rap-block-subitem">
                    <div className="rap-block-subitem-order">
                        <button type="button" className="rap-order-btn" onClick={() => onChange(moveItem(items, i, -1))} disabled={i === 0}><ChevronUp size={12} /></button>
                        <button type="button" className="rap-order-btn" onClick={() => onChange(moveItem(items, i, 1))} disabled={i === items.length - 1}><ChevronDown size={12} /></button>
                    </div>
                    <div className="rap-block-subitem-body">
                        <div className="rap-field-row">
                            <input className="rap-input" value={it.title} onChange={e => update(i, { title: e.target.value })} placeholder="Título do capítulo" />
                            <input className="rap-input rap-input-price" type="number" min={0} max={100} value={it.progress}
                                onChange={e => update(i, { progress: Number(e.target.value) })} placeholder="% progresso" />
                        </div>
                        <div className="rap-checks-row">
                            <label className="rap-check"><input type="checkbox" checked={it.hasQuiz} onChange={e => update(i, { hasQuiz: e.target.checked })} /> Tem quiz</label>
                            <label className="rap-check"><input type="radio" name="apt-nav-active" checked={it.active} onChange={() => setActive(i)} /> Ativo no índice</label>
                        </div>
                    </div>
                    <button type="button" className="rap-dbe-del" onClick={() => remove(i)}><X size={12} /></button>
                </div>
            ))}
            <button type="button" className="rap-dbe-add-li" onClick={add}><Plus size={11} /> Item do índice</button>
        </div>
    );
};

const FlashcardsDemoCardsEditor: React.FC<{
    cards: FlashcardsDemoCard[];
    onChange: (cards: FlashcardsDemoCard[]) => void;
}> = ({ cards, onChange }) => {
    const update = (i: number, patch: Partial<FlashcardsDemoCard>) => {
        const next = [...cards]; next[i] = { ...next[i], ...patch }; onChange(next);
    };
    const add = () => onChange([...cards, { id: genId(), front: '', back: '', tag: '' }]);
    const remove = (i: number) => onChange(cards.filter((_, idx) => idx !== i));

    return (
        <div className="rap-block-list">
            {cards.map((c, i) => (
                <div key={c.id} className="rap-block-subitem">
                    <div className="rap-block-subitem-order">
                        <button type="button" className="rap-order-btn" onClick={() => onChange(moveItem(cards, i, -1))} disabled={i === 0}><ChevronUp size={12} /></button>
                        <button type="button" className="rap-order-btn" onClick={() => onChange(moveItem(cards, i, 1))} disabled={i === cards.length - 1}><ChevronDown size={12} /></button>
                    </div>
                    <div className="rap-block-subitem-body">
                        <input className="rap-input" value={c.tag} onChange={e => update(i, { tag: e.target.value })} placeholder="Tag (ex: Sistema Cardiovascular)" />
                        <textarea className="rap-textarea" rows={2} value={c.front} onChange={e => update(i, { front: e.target.value })} placeholder="Pergunta (frente)" />
                        <textarea className="rap-textarea" rows={2} value={c.back} onChange={e => update(i, { back: e.target.value })} placeholder="Resposta (verso)" />
                    </div>
                    <button type="button" className="rap-dbe-del" onClick={() => remove(i)} disabled={cards.length <= 3}><X size={12} /></button>
                </div>
            ))}
            <button type="button" className="rap-dbe-add-li" onClick={add}><Plus size={11} /> Card</button>
            {cards.length < 3 && <p className="rap-hint">Recomendado pelo menos 3 cards para a demo ciclar bem.</p>}
        </div>
    );
};

const DemoPillarsEditor: React.FC<{
    pillars: DemoPillar[];
    onChange: (pillars: DemoPillar[]) => void;
}> = ({ pillars, onChange }) => {
    const [iconPickerIdx, setIconPickerIdx] = useState<number | null>(null);
    const update = (i: number, patch: Partial<DemoPillar>) => {
        const next = [...pillars]; next[i] = { ...next[i], ...patch }; onChange(next);
    };
    const add = () => onChange([...pillars, { id: genId(), icon: 'Star', title: '', text: '' }]);
    const remove = (i: number) => onChange(pillars.filter((_, idx) => idx !== i));

    return (
        <div className="rap-block-list">
            {pillars.map((pillar, i) => {
                const IC = DESC_ICONS.find(x => x.name === pillar.icon)?.component ?? BookOpen;
                return (
                    <div key={pillar.id} className="rap-block-subitem">
                        <div className="rap-block-subitem-order">
                            <button type="button" className="rap-order-btn" onClick={() => onChange(moveItem(pillars, i, -1))} disabled={i === 0}><ChevronUp size={12} /></button>
                            <button type="button" className="rap-order-btn" onClick={() => onChange(moveItem(pillars, i, 1))} disabled={i === pillars.length - 1}><ChevronDown size={12} /></button>
                        </div>
                        <div className="rap-block-subitem-body">
                            <div className="rap-field-row">
                                <div className="rap-dbe-icon-wrap">
                                    <button type="button" className="rap-dbe-icon-btn"
                                        onClick={() => setIconPickerIdx(iconPickerIdx === i ? null : i)}>
                                        <IC size={14} />
                                    </button>
                                    {iconPickerIdx === i && (
                                        <div className="rap-dbe-picker">
                                            {DESC_ICONS.map(ic => {
                                                const C = ic.component;
                                                return (
                                                    <button key={ic.name} type="button"
                                                        className={`rap-dbe-pick ${pillar.icon === ic.name ? 'active' : ''}`}
                                                        title={ic.label}
                                                        onClick={() => { update(i, { icon: ic.name }); setIconPickerIdx(null); }}>
                                                        <C size={13} />
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                <input className="rap-input" value={pillar.title} onChange={e => update(i, { title: e.target.value })} placeholder="Título do pilar" />
                            </div>
                            <textarea className="rap-textarea" rows={2} value={pillar.text} onChange={e => update(i, { text: e.target.value })} placeholder="Texto curto explicando o recurso..." />
                        </div>
                        <button type="button" className="rap-dbe-del" onClick={() => remove(i)}><X size={12} /></button>
                    </div>
                );
            })}
            <button type="button" className="rap-dbe-add-li" onClick={add}><Plus size={11} /> Pilar</button>
        </div>
    );
};

const DemoClosingEditor: React.FC<{
    closing: DemoClosing;
    onChange: (closing: DemoClosing) => void;
}> = ({ closing, onChange }) => {
    const updateItem = (i: number, text: string) => {
        const next = [...closing.items]; next[i] = text; onChange({ ...closing, items: next });
    };
    const addItem = () => onChange({ ...closing, items: [...closing.items, ''] });
    const removeItem = (i: number) => onChange({ ...closing, items: closing.items.filter((_, idx) => idx !== i) });
    const moveClosingItem = (i: number, dir: -1 | 1) => onChange({ ...closing, items: moveItem(closing.items, i, dir) });

    return (
        <>
            <div className="rap-field-row">
                <div className="rap-field">
                    <label className="rap-label">Frase de fechamento</label>
                    <input className="rap-input" value={closing.lead} onChange={e => onChange({ ...closing, lead: e.target.value })} placeholder='ex: "Não é repetição."' />
                </div>
                <div className="rap-field">
                    <label className="rap-label">Ênfase (negrito)</label>
                    <input className="rap-input" value={closing.emphasis} onChange={e => onChange({ ...closing, emphasis: e.target.value })} placeholder='ex: "É reorganização inteligente."' />
                </div>
            </div>
            <div className="rap-field">
                <label className="rap-label">Lista de diferenciais (opcional)</label>
                <div className="rap-block-list">
                    {closing.items.map((item, i) => (
                        <div key={i} className="rap-block-subitem">
                            <div className="rap-block-subitem-order">
                                <button type="button" className="rap-order-btn" onClick={() => moveClosingItem(i, -1)} disabled={i === 0}><ChevronUp size={12} /></button>
                                <button type="button" className="rap-order-btn" onClick={() => moveClosingItem(i, 1)} disabled={i === closing.items.length - 1}><ChevronDown size={12} /></button>
                            </div>
                            <div className="rap-block-subitem-body">
                                <input className="rap-input" value={item} onChange={e => updateItem(i, e.target.value)} placeholder="Diferencial..." />
                            </div>
                            <button type="button" className="rap-dbe-del" onClick={() => removeItem(i)}><X size={12} /></button>
                        </div>
                    ))}
                    <button type="button" className="rap-dbe-add-li" onClick={addItem}><Plus size={11} /> Diferencial</button>
                </div>
            </div>
        </>
    );
};

const ApostilaDemoEditor: React.FC<{
    data: ApostilaDemoData;
    onChange: (patch: Partial<ApostilaDemoData>) => void;
}> = ({ data, onChange }) => (
    <>
        <div className="rap-field">
            <label className="rap-label">Label (acima do título)</label>
            <input className="rap-input" value={data.label} onChange={e => onChange({ label: e.target.value })} placeholder="ex: Apostilas Inteligentes" />
        </div>
        <div className="rap-field">
            <label className="rap-label">Headline</label>
            <textarea className="rap-textarea" rows={2} value={data.headline} onChange={e => onChange({ headline: e.target.value })} placeholder="Título grande do bloco..." />
        </div>
        <div className="rap-field">
            <label className="rap-label">Subheadline (opcional)</label>
            <textarea className="rap-textarea" rows={2} value={data.subheadline} onChange={e => onChange({ subheadline: e.target.value })} />
        </div>
        <div className="rap-field-row">
            <div className="rap-field">
                <label className="rap-label">Matéria (breadcrumb)</label>
                <input className="rap-input" value={data.breadcrumbSubject} onChange={e => onChange({ breadcrumbSubject: e.target.value })} placeholder="ex: Neurociência" />
            </div>
            <div className="rap-field">
                <label className="rap-label">Capítulo (breadcrumb)</label>
                <input className="rap-input" value={data.breadcrumbChapter} onChange={e => onChange({ breadcrumbChapter: e.target.value })} placeholder="ex: Capítulo 2 · Sinapses Químicas" />
            </div>
        </div>
        <div className="rap-field">
            <label className="rap-label">Título do capítulo</label>
            <input className="rap-input" value={data.chapterTitle} onChange={e => onChange({ chapterTitle: e.target.value })} />
        </div>
        <div className="rap-field">
            <label className="rap-label">Texto (partes editáveis)</label>
            <ApostilaSegmentsEditor segments={data.segments} onChange={segments => onChange({ segments })} />
            <p className="rap-hint">Marque "Grifar" na parte que deve ser destacada e preencha "Nota" na parte que deve receber uma anotação (vazio = sem nota). "Badge de quiz" mostra um botão de quiz ao lado do trecho.</p>
        </div>
        <div className="rap-field">
            <label className="rap-label">Pergunta do quiz</label>
            <textarea className="rap-textarea" rows={2} value={data.quizQuestion} onChange={e => onChange({ quizQuestion: e.target.value })} />
        </div>
        <div className="rap-field">
            <label className="rap-label">Alternativas (marque a correta)</label>
            <ApostilaQuizOptionsEditor options={data.quizOptions} onChange={quizOptions => onChange({ quizOptions })} />
        </div>
        <div className="rap-field">
            <label className="rap-label">Índice de capítulos (painel lateral da demo)</label>
            <ApostilaNavItemsEditor items={data.navItems} onChange={navItems => onChange({ navItems })} />
        </div>
        <div className="rap-field">
            <label className="rap-label">Pilares (coluna ao lado da demo)</label>
            <DemoPillarsEditor pillars={data.pillars} onChange={pillars => onChange({ pillars })} />
        </div>
        <div className="rap-field">
            <label className="rap-label">Fechamento</label>
            <DemoClosingEditor closing={data.closing} onChange={closing => onChange({ closing })} />
        </div>
    </>
);

const FlashcardsDemoEditor: React.FC<{
    data: FlashcardsDemoData;
    onChange: (patch: Partial<FlashcardsDemoData>) => void;
}> = ({ data, onChange }) => (
    <>
        <div className="rap-field">
            <label className="rap-label">Label (acima do título)</label>
            <input className="rap-input" value={data.label} onChange={e => onChange({ label: e.target.value })} placeholder="ex: Flashcards Inteligentes" />
        </div>
        <div className="rap-field">
            <label className="rap-label">Headline</label>
            <textarea className="rap-textarea" rows={2} value={data.headline} onChange={e => onChange({ headline: e.target.value })} placeholder="Título grande do bloco..." />
        </div>
        <div className="rap-field">
            <label className="rap-label">Subheadline (opcional)</label>
            <textarea className="rap-textarea" rows={2} value={data.subheadline} onChange={e => onChange({ subheadline: e.target.value })} />
        </div>
        <div className="rap-field">
            <label className="rap-label">Cards de demonstração</label>
            <FlashcardsDemoCardsEditor cards={data.cards} onChange={cards => onChange({ cards })} />
        </div>
        <div className="rap-field">
            <label className="rap-label">Pilares (coluna ao lado da demo)</label>
            <DemoPillarsEditor pillars={data.pillars} onChange={pillars => onChange({ pillars })} />
        </div>
        <div className="rap-field">
            <label className="rap-label">Fechamento</label>
            <DemoClosingEditor closing={data.closing} onChange={closing => onChange({ closing })} />
        </div>
    </>
);

const MetodoWorldEditor: React.FC<{
    world: MetodoDemoWorld;
    onChange: (patch: Partial<MetodoDemoWorld>) => void;
}> = ({ world, onChange }) => {
    const [iconPickerOpen, setIconPickerOpen] = useState(false);
    const IC = DESC_ICONS.find(x => x.name === world.icon)?.component ?? BookOpen;
    return (
        <div className="rap-block-subitem">
            <div className="rap-block-subitem-body">
                <div className="rap-field-row">
                    <div className="rap-dbe-icon-wrap">
                        <button type="button" className="rap-dbe-icon-btn" onClick={() => setIconPickerOpen(v => !v)}>
                            <IC size={14} />
                        </button>
                        {iconPickerOpen && (
                            <div className="rap-dbe-picker">
                                {DESC_ICONS.map(ic => {
                                    const C = ic.component;
                                    return (
                                        <button key={ic.name} type="button"
                                            className={`rap-dbe-pick ${world.icon === ic.name ? 'active' : ''}`}
                                            title={ic.label}
                                            onClick={() => { onChange({ icon: ic.name }); setIconPickerOpen(false); }}>
                                            <C size={13} />
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <input className="rap-input" value={world.title} onChange={e => onChange({ title: e.target.value })} placeholder="Título (ex: Flashcards)" />
                </div>
                <input className="rap-input" value={world.subtitle} onChange={e => onChange({ subtitle: e.target.value })} placeholder="Subtítulo curto (ex: Revisão)" />
            </div>
        </div>
    );
};

const MetodoStepsEditor: React.FC<{
    steps: string[];
    onChange: (steps: string[]) => void;
}> = ({ steps, onChange }) => {
    const update = (i: number, value: string) => {
        const next = [...steps]; next[i] = value; onChange(next);
    };
    const add = () => onChange([...steps, '']);
    const remove = (i: number) => onChange(steps.filter((_, idx) => idx !== i));

    return (
        <div className="rap-block-list">
            {steps.map((step, i) => (
                <div key={i} className="rap-block-subitem">
                    <div className="rap-block-subitem-order">
                        <button type="button" className="rap-order-btn" onClick={() => onChange(moveItem(steps, i, -1))} disabled={i === 0}><ChevronUp size={12} /></button>
                        <button type="button" className="rap-order-btn" onClick={() => onChange(moveItem(steps, i, 1))} disabled={i === steps.length - 1}><ChevronDown size={12} /></button>
                    </div>
                    <div className="rap-block-subitem-body">
                        <input className="rap-input" value={step} onChange={e => update(i, e.target.value)} placeholder='ex: "Aprenda."' />
                    </div>
                    <button type="button" className="rap-dbe-del" onClick={() => remove(i)}><X size={12} /></button>
                </div>
            ))}
            <button type="button" className="rap-dbe-add-li" onClick={add}><Plus size={11} /> Passo</button>
        </div>
    );
};

const MetodoDemoEditor: React.FC<{
    data: MetodoDemoData;
    onChange: (patch: Partial<MetodoDemoData>) => void;
}> = ({ data, onChange }) => (
    <>
        <div className="rap-field">
            <label className="rap-label">Label (acima do título)</label>
            <input className="rap-input" value={data.label} onChange={e => onChange({ label: e.target.value })} placeholder="ex: Método GRAIA" />
        </div>
        <div className="rap-field">
            <label className="rap-label">Headline</label>
            <textarea className="rap-textarea" rows={2} value={data.headline} onChange={e => onChange({ headline: e.target.value })} placeholder="Título grande do bloco..." />
        </div>
        <div className="rap-field">
            <label className="rap-label">Subheadline (opcional)</label>
            <textarea className="rap-textarea" rows={2} value={data.subheadline} onChange={e => onChange({ subheadline: e.target.value })} />
        </div>
        <div className="rap-field">
            <label className="rap-label">Nome do método</label>
            <input className="rap-input" value={data.methodName} onChange={e => onChange({ methodName: e.target.value })} placeholder="ex: Método GRAIA" />
        </div>
        <div className="rap-field">
            <label className="rap-label">Mundo da esquerda (ex: Flashcards)</label>
            <MetodoWorldEditor world={data.leftWorld} onChange={patch => onChange({ leftWorld: { ...data.leftWorld, ...patch } })} />
        </div>
        <div className="rap-field">
            <label className="rap-label">Mundo da direita (ex: Apostilas)</label>
            <MetodoWorldEditor world={data.rightWorld} onChange={patch => onChange({ rightWorld: { ...data.rightWorld, ...patch } })} />
        </div>
        <div className="rap-field">
            <label className="rap-label">Passos do método</label>
            <MetodoStepsEditor steps={data.methodSteps} onChange={methodSteps => onChange({ methodSteps })} />
        </div>
        <div className="rap-field">
            <label className="rap-label">Trecho de apostila (prévia)</label>
            <textarea className="rap-textarea" rows={3} value={data.demoParagraph} onChange={e => onChange({ demoParagraph: e.target.value })} />
        </div>
        <div className="rap-field">
            <label className="rap-label">Trecho a destacar</label>
            <input className="rap-input" value={data.demoHighlightText} onChange={e => onChange({ demoHighlightText: e.target.value })} />
            <p className="rap-hint">Precisa ser um trecho copiado exatamente de dentro do texto acima — usado como grifo na prévia. Se não for encontrado, nenhum grifo aparece.</p>
        </div>
        <div className="rap-field">
            <label className="rap-label">Nota (o que vira flashcard)</label>
            <input className="rap-input" value={data.demoNoteText} onChange={e => onChange({ demoNoteText: e.target.value })} />
        </div>
        <div className="rap-field-row">
            <div className="rap-field">
                <label className="rap-label">Flashcard — pergunta</label>
                <textarea className="rap-textarea" rows={2} value={data.demoFlashcardFront} onChange={e => onChange({ demoFlashcardFront: e.target.value })} />
            </div>
            <div className="rap-field">
                <label className="rap-label">Flashcard — resposta</label>
                <textarea className="rap-textarea" rows={2} value={data.demoFlashcardBack} onChange={e => onChange({ demoFlashcardBack: e.target.value })} />
            </div>
        </div>
        <div className="rap-field-row">
            <div className="rap-field">
                <label className="rap-label">Domínio antes (%)</label>
                <input className="rap-input" type="number" min={0} max={100}
                    value={data.demoMasteryBefore}
                    onChange={e => onChange({ demoMasteryBefore: Number(e.target.value) })} />
            </div>
            <div className="rap-field">
                <label className="rap-label">Domínio depois (%)</label>
                <input className="rap-input" type="number" min={0} max={100}
                    value={data.demoMasteryAfter}
                    onChange={e => onChange({ demoMasteryAfter: Number(e.target.value) })} />
            </div>
        </div>
        <div className="rap-field">
            <label className="rap-label">Frase de transição (entre a demo e o fechamento)</label>
            <textarea className="rap-textarea" rows={2} value={data.closingHeadline} onChange={e => onChange({ closingHeadline: e.target.value })} />
        </div>
        <div className="rap-field">
            <label className="rap-label">Pilares (coluna ao lado da demo)</label>
            <DemoPillarsEditor pillars={data.pillars} onChange={pillars => onChange({ pillars })} />
        </div>
        <div className="rap-field">
            <label className="rap-label">Fechamento</label>
            <DemoClosingEditor closing={data.closing} onChange={closing => onChange({ closing })} />
        </div>
    </>
);

const EstudoAtivoColumnEditor: React.FC<{
    label: string;
    column: EstudoAtivoColumn;
    onChange: (patch: Partial<EstudoAtivoColumn>) => void;
}> = ({ label, column, onChange }) => (
    <div className="rap-field">
        <label className="rap-label">{label}</label>
        <input className="rap-input" value={column.title} onChange={e => onChange({ title: e.target.value })} placeholder="Título da coluna" />
        <DemoPillarsEditor pillars={column.pillars} onChange={pillars => onChange({ pillars })} />
    </div>
);

const EstudoAtivoDemoEditor: React.FC<{
    data: EstudoAtivoDemoData;
    onChange: (patch: Partial<EstudoAtivoDemoData>) => void;
}> = ({ data, onChange }) => (
    <>
        <div className="rap-field">
            <label className="rap-label">Label (acima do título)</label>
            <input className="rap-input" value={data.label} onChange={e => onChange({ label: e.target.value })} placeholder="ex: Método de estudo" />
        </div>
        <div className="rap-field">
            <label className="rap-label">Headline</label>
            <textarea className="rap-textarea" rows={2} value={data.headline} onChange={e => onChange({ headline: e.target.value })} placeholder="Título grande do bloco..." />
        </div>
        <div className="rap-field">
            <label className="rap-label">Subheadline (opcional)</label>
            <textarea className="rap-textarea" rows={2} value={data.subheadline} onChange={e => onChange({ subheadline: e.target.value })} />
        </div>
        <EstudoAtivoColumnEditor
            label="Coluna — Flashcards"
            column={data.flashcardsColumn}
            onChange={patch => onChange({ flashcardsColumn: { ...data.flashcardsColumn, ...patch } })}
        />
        <EstudoAtivoColumnEditor
            label="Coluna — Apostilas"
            column={data.apostilasColumn}
            onChange={patch => onChange({ apostilasColumn: { ...data.apostilasColumn, ...patch } })}
        />
        <div className="rap-field">
            <label className="rap-label">Palco (prévia animada) — trecho de apostila</label>
            <textarea className="rap-textarea" rows={3} value={data.stageParagraph} onChange={e => onChange({ stageParagraph: e.target.value })} />
        </div>
        <div className="rap-field">
            <label className="rap-label">Palco — trecho a destacar</label>
            <input className="rap-input" value={data.stageHighlightText} onChange={e => onChange({ stageHighlightText: e.target.value })} />
            <p className="rap-hint">Precisa ser um trecho copiado exatamente de dentro do texto acima — usado como grifo no palco. Se não for encontrado, nenhum grifo aparece.</p>
        </div>
        <div className="rap-field">
            <label className="rap-label">Palco — nota</label>
            <input className="rap-input" value={data.stageNoteText} onChange={e => onChange({ stageNoteText: e.target.value })} />
        </div>
        <div className="rap-field-row">
            <div className="rap-field">
                <label className="rap-label">Palco — flashcard (pergunta)</label>
                <textarea className="rap-textarea" rows={2} value={data.stageFlashcardFront} onChange={e => onChange({ stageFlashcardFront: e.target.value })} />
            </div>
            <div className="rap-field">
                <label className="rap-label">Palco — flashcard (resposta)</label>
                <textarea className="rap-textarea" rows={2} value={data.stageFlashcardBack} onChange={e => onChange({ stageFlashcardBack: e.target.value })} />
            </div>
        </div>
        <div className="rap-field-row">
            <div className="rap-field">
                <label className="rap-label">Palco — domínio antes (%)</label>
                <input className="rap-input" type="number" min={0} max={100}
                    value={data.stageMasteryBefore}
                    onChange={e => onChange({ stageMasteryBefore: Number(e.target.value) })} />
            </div>
            <div className="rap-field">
                <label className="rap-label">Palco — domínio depois (%)</label>
                <input className="rap-input" type="number" min={0} max={100}
                    value={data.stageMasteryAfter}
                    onChange={e => onChange({ stageMasteryAfter: Number(e.target.value) })} />
            </div>
        </div>
        <div className="rap-field">
            <label className="rap-label">Passos do fluxo</label>
            <MetodoStepsEditor steps={data.flowSteps} onChange={flowSteps => onChange({ flowSteps })} />
        </div>
        <div className="rap-field">
            <label className="rap-label">Descrição do fluxo</label>
            <textarea className="rap-textarea" rows={2} value={data.flowDescription} onChange={e => onChange({ flowDescription: e.target.value })} />
        </div>
        <div className="rap-field">
            <label className="rap-label">Pilares (rodapé opcional)</label>
            <DemoPillarsEditor pillars={data.pillars} onChange={pillars => onChange({ pillars })} />
        </div>
        <div className="rap-field">
            <label className="rap-label">Fechamento</label>
            <DemoClosingEditor closing={data.closing} onChange={closing => onChange({ closing })} />
        </div>
    </>
);

const TutoriaCardsEditor: React.FC<{
    cards: TutoriaCard[];
    onChange: (cards: TutoriaCard[]) => void;
}> = ({ cards, onChange }) => {
    const [iconPickerIdx, setIconPickerIdx] = useState<number | null>(null);

    // Renumera a sequência principal (0, 1, 2...) a partir da ORDEM do
    // array — nunca um número editável manualmente. Cards com
    // sequenceIndex !== null recebem os próximos índices em ordem; os
    // demais viram null. Chamada depois de toggle/reorder/add/remove, então
    // a numeração nunca fica dessincronizada da posição real na lista.
    const renumber = (list: TutoriaCard[]): TutoriaCard[] => {
        let idx = 0;
        return list.map(c => c.sequenceIndex !== null ? { ...c, sequenceIndex: idx++ } : { ...c, sequenceIndex: null });
    };

    const update = (i: number, patch: Partial<TutoriaCard>) => {
        const next = [...cards]; next[i] = { ...next[i], ...patch }; onChange(next);
    };
    const toggleSequence = (i: number) => {
        const next = [...cards];
        next[i] = { ...next[i], sequenceIndex: next[i].sequenceIndex !== null ? null : 0 };
        onChange(renumber(next));
    };
    const move = (i: number, dir: -1 | 1) => onChange(renumber(moveItem(cards, i, dir)));
    const add = () => onChange([...cards, { id: genId(), title: '', icon: 'Star', color: '#EDEFEE', sequenceIndex: null }]);
    const remove = (i: number) => onChange(renumber(cards.filter((_, idx) => idx !== i)));

    return (
        <div className="rap-block-list">
            {cards.map((card, i) => {
                const IC = DESC_ICONS.find(x => x.name === card.icon)?.component ?? BookOpen;
                return (
                    <div key={card.id} className="rap-block-subitem">
                        <div className="rap-block-subitem-order">
                            <button type="button" className="rap-order-btn" onClick={() => move(i, -1)} disabled={i === 0}><ChevronUp size={12} /></button>
                            <button type="button" className="rap-order-btn" onClick={() => move(i, 1)} disabled={i === cards.length - 1}><ChevronDown size={12} /></button>
                        </div>
                        <div className="rap-block-subitem-body">
                            <div className="rap-field-row">
                                <div className="rap-dbe-icon-wrap">
                                    <button type="button" className="rap-dbe-icon-btn"
                                        onClick={() => setIconPickerIdx(iconPickerIdx === i ? null : i)}>
                                        <IC size={14} />
                                    </button>
                                    {iconPickerIdx === i && (
                                        <div className="rap-dbe-picker">
                                            {DESC_ICONS.map(ic => {
                                                const C = ic.component;
                                                return (
                                                    <button key={ic.name} type="button"
                                                        className={`rap-dbe-pick ${card.icon === ic.name ? 'active' : ''}`}
                                                        title={ic.label}
                                                        onClick={() => { update(i, { icon: ic.name }); setIconPickerIdx(null); }}>
                                                        <C size={13} />
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                <input className="rap-input" value={card.title} onChange={e => update(i, { title: e.target.value })} placeholder="Título do cartão" />
                            </div>
                            <div className="rap-tut-row">
                                <input
                                    type="color"
                                    className="rap-color-swatch"
                                    value={card.color}
                                    onChange={e => update(i, { color: e.target.value })}
                                    title="Cor do cartão"
                                />
                                <label className="rap-check">
                                    <input type="checkbox" checked={card.sequenceIndex !== null} onChange={() => toggleSequence(i)} />
                                    Faz parte da sequência{card.sequenceIndex !== null ? ` — ${card.sequenceIndex + 1}º` : ''}
                                </label>
                            </div>
                        </div>
                        <button type="button" className="rap-dbe-del" onClick={() => remove(i)}><X size={12} /></button>
                    </div>
                );
            })}
            <button type="button" className="rap-dbe-add-li" onClick={add}><Plus size={11} /> Cartão</button>
        </div>
    );
};

const TutoriaDemoEditor: React.FC<{
    data: TutoriaDemoData;
    onChange: (patch: Partial<TutoriaDemoData>) => void;
}> = ({ data, onChange }) => (
    <>
        <div className="rap-field">
            <label className="rap-label">Label (acima do título)</label>
            <input className="rap-input" value={data.label} onChange={e => onChange({ label: e.target.value })} placeholder="ex: Prepare-se melhor" />
        </div>
        <div className="rap-field">
            <label className="rap-label">Headline</label>
            <textarea className="rap-textarea" rows={2} value={data.headline} onChange={e => onChange({ headline: e.target.value })} placeholder="Título grande do bloco..." />
        </div>
        <div className="rap-field">
            <label className="rap-label">Subheadline (opcional)</label>
            <textarea className="rap-textarea" rows={2} value={data.subheadline} onChange={e => onChange({ subheadline: e.target.value })} />
        </div>
        <div className="rap-field">
            <label className="rap-label">Cartões da mesa</label>
            <TutoriaCardsEditor cards={data.cards} onChange={cards => onChange({ cards })} />
            <p className="rap-hint">Só cartões com "Faz parte da sequência" marcado aparecem no bloco na página do produto — os demais ficam salvos aqui, mas não são exibidos. A posição (1º, 2º...) é calculada automaticamente pela ordem deles nesta lista (use as setas pra reordenar).</p>
        </div>
        <div className="rap-field-row">
            <div className="rap-field">
                <label className="rap-label">Frase de fechamento</label>
                <input className="rap-input" value={data.closingLead} onChange={e => onChange({ closingLead: e.target.value })} placeholder='ex: "Toda tutoria segue uma linha de raciocínio."' />
            </div>
            <div className="rap-field">
                <label className="rap-label">Ênfase (itálico)</label>
                <input className="rap-input" value={data.closingEmphasis} onChange={e => onChange({ closingEmphasis: e.target.value })} placeholder='ex: "A organização muda o jeito de estudar."' />
            </div>
        </div>
    </>
);

const VideoDemoEditor: React.FC<{
    data: VideoDemoData;
    onChange: (patch: Partial<VideoDemoData>) => void;
}> = ({ data, onChange }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const posterInputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [posterUploading, setPosterUploading] = useState(false);
    const [posterUploadError, setPosterUploadError] = useState('');

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = ''; // permite selecionar o mesmo arquivo de novo depois
        if (!file) return;
        setUploadError('');
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('video', file);
            const res = await fetch('/api/upload/marketing-video-cloudinary', {
                method: 'POST',
                headers: { Authorization: `Bearer ${tok()}` },
                body: formData,
            });
            const result = await res.json();
            if (!res.ok) throw new Error(result.message || 'Erro ao enviar vídeo');
            onChange({ videoUrl: result.url, videoPublicId: result.public_id });
        } catch (err: any) {
            setUploadError(err.message || 'Erro ao enviar vídeo');
        } finally {
            setUploading(false);
        }
    };

    const handleRemove = async () => {
        const publicId = data.videoPublicId;
        onChange({ videoUrl: '', videoPublicId: '' });
        if (publicId) {
            // Best-effort — o form já reflete a remoção mesmo se essa chamada falhar
            // (o vídeo antigo só fica órfão no Cloudinary, não afeta o produto).
            fetch(`/api/upload/cloudinary-media/${encodeURIComponent(publicId)}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${tok()}` },
            }).catch(() => {});
        }
    };

    const handlePosterFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setPosterUploadError('');
        setPosterUploading(true);
        try {
            const formData = new FormData();
            formData.append('image', file);
            const res = await fetch('/api/upload/marketing-video-poster-cloudinary', {
                method: 'POST',
                headers: { Authorization: `Bearer ${tok()}` },
                body: formData,
            });
            const result = await res.json();
            if (!res.ok) throw new Error(result.message || 'Erro ao enviar capa');
            onChange({ posterUrl: result.url, posterPublicId: result.public_id });
        } catch (err: any) {
            setPosterUploadError(err.message || 'Erro ao enviar capa');
        } finally {
            setPosterUploading(false);
        }
    };

    const handleRemovePoster = async () => {
        const publicId = data.posterPublicId;
        onChange({ posterUrl: '', posterPublicId: '' });
        if (publicId) {
            fetch(`/api/upload/cloudinary-media/${encodeURIComponent(publicId)}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${tok()}` },
            }).catch(() => {});
        }
    };

    return (
        <>
            <div className="rap-field">
                <label className="rap-label">Label (acima do título, opcional)</label>
                <input className="rap-input" value={data.label} onChange={e => onChange({ label: e.target.value })} placeholder="ex: Veja como funciona" />
            </div>
            <div className="rap-field">
                <label className="rap-label">Headline</label>
                <textarea className="rap-textarea" rows={2} value={data.headline} onChange={e => onChange({ headline: e.target.value })} placeholder="Título grande do bloco..." />
            </div>
            <div className="rap-field">
                <label className="rap-label">Subheadline (opcional)</label>
                <textarea className="rap-textarea" rows={2} value={data.subheadline} onChange={e => onChange({ subheadline: e.target.value })} />
            </div>
            <div className="rap-field">
                <label className="rap-label">Vídeo</label>
                {data.videoUrl ? (
                    <div className="rap-video-preview">
                        <video src={data.videoUrl} controls className="rap-video-preview-el" />
                        <button type="button" className="rap-btn-ghost" onClick={handleRemove}>
                            <X size={12} /> Remover vídeo
                        </button>
                    </div>
                ) : (
                    <>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
                            onChange={handleFileChange}
                            disabled={uploading}
                            style={{ display: 'none' }}
                        />
                        <button
                            type="button"
                            className="rap-dbe-add-li"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                        >
                            {uploading
                                ? <><span className="rap-spinner rap-spinner--sm" /> Enviando...</>
                                : <><Plus size={11} /> Enviar vídeo</>}
                        </button>
                    </>
                )}
                {uploadError && <p className="rap-field-error">{uploadError}</p>}
                <p className="rap-hint">
                    MP4, WEBM ou MOV, até 100MB. Sem autoplay: o vídeo fica parado com botão de play até o
                    visitante clicar.
                </p>
            </div>
            <div className="rap-field">
                <label className="rap-label">Capa do vídeo (opcional)</label>
                {data.posterUrl ? (
                    <div className="rap-video-preview">
                        <img src={data.posterUrl} alt="Capa do vídeo" className="rap-video-preview-el" />
                        <button type="button" className="rap-btn-ghost" onClick={handleRemovePoster}>
                            <X size={12} /> Remover capa
                        </button>
                    </div>
                ) : (
                    <>
                        <input
                            ref={posterInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            onChange={handlePosterFileChange}
                            disabled={posterUploading}
                            style={{ display: 'none' }}
                        />
                        <button
                            type="button"
                            className="rap-dbe-add-li"
                            onClick={() => posterInputRef.current?.click()}
                            disabled={posterUploading}
                        >
                            {posterUploading
                                ? <><span className="rap-spinner rap-spinner--sm" /> Enviando...</>
                                : <><Plus size={11} /> Enviar imagem de capa</>}
                        </button>
                    </>
                )}
                {posterUploadError && <p className="rap-field-error">{posterUploadError}</p>}
                <p className="rap-hint">
                    Imagem exibida por trás do botão de play, antes do visitante clicar. Se não enviar nada, a
                    capa vira um quadro tirado automaticamente do próprio vídeo — funciona, mas costuma pegar um
                    instante aleatório. Enviar uma print/imagem própria (ex: uma tela bonita da plataforma) fica
                    mais bonito e previsível.
                </p>
            </div>
        </>
    );
};

const BLOCK_TYPE_LABEL: Record<ContentBlock['type'], string> = {
    apostila_demo: 'Apostila Interativa',
    flashcards_demo: 'Flashcards Adaptativos',
    metodo_demo: 'Método GRAIA',
    estudo_ativo_demo: 'Estudo Ativo',
    tutoria_demo: 'Tutoria',
    video_demo: 'Vídeo',
};

interface SortableBlockItemProps {
    block: ContentBlock;
    expanded: boolean;
    onToggleExpand: () => void;
    onToggleEnabled: () => void;
    onUpdateData: (patch: Partial<ApostilaDemoData> | Partial<FlashcardsDemoData> | Partial<MetodoDemoData> | Partial<EstudoAtivoDemoData> | Partial<TutoriaDemoData> | Partial<VideoDemoData>) => void;
    onUpdateBackgroundVariant: (variant: 'white' | 'green' | 'light' | 'dark') => void;
    onRemove: () => void;
    deleteConfirm: boolean;
    onRequestDelete: () => void;
    onCancelDelete: () => void;
}

const SortableBlockItem: React.FC<SortableBlockItemProps> = ({
    block, expanded, onToggleExpand, onToggleEnabled, onUpdateData, onUpdateBackgroundVariant, onRemove,
    deleteConfirm, onRequestDelete, onCancelDelete,
}) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
    const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

    const summary = block.type === 'apostila_demo'
        ? ((block.data as ApostilaDemoData).chapterTitle || 'Sem título')
        : block.type === 'flashcards_demo'
        ? `${(block.data as FlashcardsDemoData).cards.length} card(s)`
        : block.type === 'metodo_demo'
        ? ((block.data as MetodoDemoData).methodName || 'Sem nome')
        : block.type === 'estudo_ativo_demo'
        ? 'Flashcards + Apostilas'
        : block.type === 'tutoria_demo'
        ? (() => {
            const tutCards = (block.data as TutoriaDemoData).cards;
            const visible = tutCards.filter(c => c.sequenceIndex !== null).length;
            return `${tutCards.length} cartões, ${visible} visíveis`;
        })()
        : ((block.data as VideoDemoData).videoUrl ? 'Vídeo enviado' : 'Sem vídeo ainda');

    return (
        <div ref={setNodeRef} style={style} className="rap-block-item">
            <div className="rap-block-item-row">
                <div className="rap-block-drag" {...attributes} {...listeners}><GripVertical size={16} /></div>
                <div className="rap-block-item-icon">
                    {block.type === 'apostila_demo' ? <ScrollText size={16} />
                        : block.type === 'flashcards_demo' ? <Zap size={16} />
                        : block.type === 'metodo_demo' ? <Sparkles size={16} />
                        : block.type === 'estudo_ativo_demo' ? <GraduationCap size={16} />
                        : block.type === 'tutoria_demo' ? <Users size={16} />
                        : <Video size={16} />}
                </div>
                <div className="rap-block-item-text">
                    <span className="rap-block-item-type">{BLOCK_TYPE_LABEL[block.type]}</span>
                    <span className="rap-block-item-summary">{summary}</span>
                </div>
                <button type="button" className={`rap-toggle ${block.enabled ? 'on' : ''}`} onClick={onToggleEnabled} title={block.enabled ? 'Desabilitar' : 'Habilitar'}>
                    <span className="rap-toggle-knob" />
                </button>
                <button type="button" className="rap-btn-icon" onClick={onToggleExpand} title={expanded ? 'Recolher' : 'Editar conteúdo'}>
                    {expanded ? <ChevronUp size={14} /> : <Edit3 size={14} />}
                </button>
                {deleteConfirm ? (
                    <div className="rap-modal-delete-confirm">
                        <button className="rap-confirm-yes" onClick={onRemove}>Excluir</button>
                        <button className="rap-confirm-no" onClick={onCancelDelete}>Cancelar</button>
                    </div>
                ) : (
                    <button type="button" className="rap-btn-icon rap-btn-icon--danger" onClick={onRequestDelete} title="Remover bloco">
                        <Trash2 size={14} />
                    </button>
                )}
            </div>
            {expanded && (
                <div className="rap-block-edit-form">
                    <div className="rap-field">
                        <label className="rap-label">Cor de fundo da seção</label>
                        <div className="rap-type-chips">
                            <button type="button"
                                className={`rap-type-chip${block.backgroundVariant === 'white' ? ' active' : ''}`}
                                onClick={() => onUpdateBackgroundVariant('white')}>
                                Branco
                            </button>
                            <button type="button"
                                className={`rap-type-chip${block.backgroundVariant === 'light' ? ' active' : ''}`}
                                onClick={() => onUpdateBackgroundVariant('light')}>
                                Verde claro
                            </button>
                            <button type="button"
                                className={`rap-type-chip${block.backgroundVariant === 'green' ? ' active' : ''}`}
                                onClick={() => onUpdateBackgroundVariant('green')}>
                                Verde
                            </button>
                            <button type="button"
                                className={`rap-type-chip${block.backgroundVariant === 'dark' ? ' active' : ''}`}
                                onClick={() => onUpdateBackgroundVariant('dark')}>
                                Verde escuro
                            </button>
                        </div>
                    </div>
                    {block.type === 'apostila_demo' && (
                        <ApostilaDemoEditor data={block.data as ApostilaDemoData} onChange={onUpdateData} />
                    )}
                    {block.type === 'flashcards_demo' && (
                        <FlashcardsDemoEditor data={block.data as FlashcardsDemoData} onChange={onUpdateData} />
                    )}
                    {block.type === 'metodo_demo' && (
                        <MetodoDemoEditor data={block.data as MetodoDemoData} onChange={onUpdateData} />
                    )}
                    {block.type === 'estudo_ativo_demo' && (
                        <EstudoAtivoDemoEditor data={block.data as EstudoAtivoDemoData} onChange={onUpdateData} />
                    )}
                    {block.type === 'tutoria_demo' && (
                        <TutoriaDemoEditor data={block.data as TutoriaDemoData} onChange={onUpdateData} />
                    )}
                    {block.type === 'video_demo' && (
                        <VideoDemoEditor data={block.data as VideoDemoData} onChange={onUpdateData} />
                    )}
                </div>
            )}
        </div>
    );
};

// ── Card de subdeck arrastável (ordem exibida na ProductPage) ───────

interface SortableSubdeckCardProps {
    sd: SubdeckItem;
    isOpen: boolean;
    onToggleOpen: () => void;
    descValue: string;
    descPreviewText: string;
    isDescOpen: boolean;
    onToggleDesc: () => void;
    onChangeDesc: (v: string) => void;
    previewLink: string;
    onEditLink: () => void;
    /** Controle extra no cabeçalho do card, antes do chevron — usado pelo modo
        "Personalizado" pra deixar escolher a seção deste item ali mesmo. */
    headerExtra?: React.ReactNode;
}

const SortableSubdeckCard: React.FC<SortableSubdeckCardProps> = ({
    sd, isOpen, onToggleOpen, descValue, descPreviewText, isDescOpen, onToggleDesc, onChangeDesc, previewLink, onEditLink, headerExtra,
}) => {
    // Ordem não é mais editável aqui: segue a da RyanFieldPage (space.subdeckRefs,
    // aplicada no backend) — arrastar só desalinharia o painel da página pública
    const { setNodeRef, transform, transition, isDragging } = useSortable({ id: sd._id, disabled: true });
    const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
    const isApostila = sd.type === 'apostila';
    const IconC = getRyanMedicalIcon(sd.iconId) ?? (isApostila ? FileText : BookOpen);
    const hasExtras = !!previewLink || !!descPreviewText;

    return (
        <div ref={setNodeRef} style={style}
            className={`rap-subdeck-config rap-subdeck-config--${isApostila ? 'apostila' : 'flash'}${isOpen ? ' rap-subdeck-config--open' : ''}`}
        >
            {/* Cabeçalho inteiro é o gatilho do dropdown — a alça de arrastar
                para a propagação do clique pra não abrir/fechar o card sempre
                que o admin só queria reordenar. */}
            <div className="rap-subdeck-header" role="button" tabIndex={0} onClick={onToggleOpen}
                onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onToggleOpen()}
            >
                <span className="rap-subdeck-icon-badge" title="Ícone definido no campo de estudo">
                    <IconC size={14} />
                </span>
                <span className="rap-subdeck-name">{sd.name}</span>
                {!isOpen && hasExtras && <span className="rap-subdeck-extras-dot" title="Tem link e/ou descrição definidos" />}
                <span className="rap-subdeck-space">{sd.spaceName}</span>
                {headerExtra && <span onClick={e => e.stopPropagation()}>{headerExtra}</span>}
                <ChevronDown size={13} className={`rap-subdeck-chevron${isOpen ? ' rap-subdeck-chevron--open' : ''}`} />
            </div>
            {/* Renderizado condicionalmente (não sempre-no-DOM com max-height como
                na ProductPage) — o picker de ícone do DescBlockEditorInline é um
                popover absoluto que precisa poder escapar da caixa do card; ver
                nota em RyanAdminProducts.css .rap-subdeck-fields-inner. */}
            {isOpen && (
                <div className="rap-subdeck-fields-inner">
                    <button type="button" className="rap-subdeck-link-btn" onClick={onEditLink}>
                        <ExternalLink size={12} />
                        {previewLink ? 'Editar link de prévia' : 'Adicionar link de prévia'}
                        {previewLink && <span className="rap-subdeck-link-badge">definido</span>}
                    </button>
                    <button type="button" className="rap-subdeck-desc-toggle" onClick={onToggleDesc}>
                        {isDescOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                        {isDescOpen ? 'Fechar descrição' : (descPreviewText ? 'Editar descrição' : 'Adicionar descrição')}
                        {!isDescOpen && descPreviewText && <span className="rap-subdeck-desc-preview">— {descPreviewText}</span>}
                    </button>
                    {isDescOpen && (
                        <div className="rap-field">
                            <DescBlockEditorInline value={descValue} onChange={onChangeDesc} />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ── Seção de subdecks (aba OU Apostilas/Flashcards) com dropdown próprio —
// mesmo cabeçalho colapsável reaproveitado nos dois modos de organização,
// pra não divergir o comportamento de abrir/fechar entre eles. ──────────

const SubdeckSectionShell: React.FC<{
    icon: React.ReactNode;
    label: string;
    count: number;
    isOpen: boolean;
    onToggleOpen: () => void;
    headerExtra?: React.ReactNode;
    children: React.ReactNode;
}> = ({ icon, label, count, isOpen, onToggleOpen, headerExtra, children }) => (
    <div className={`rap-subdeck-section${isOpen ? ' rap-subdeck-section--open' : ''}`}>
        <div className="rap-subdeck-section-label" role="button" tabIndex={0} onClick={onToggleOpen}
            onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onToggleOpen()}
        >
            {icon}
            {label}
            <span className="rap-subdeck-section-count">{count}</span>
            {headerExtra}
            <ChevronDown size={14} className={`rap-subdeck-section-chevron${isOpen ? ' rap-subdeck-section-chevron--open' : ''}`} />
        </div>
        {/* Renderizado condicionalmente — mesmo motivo do card de subdeck (ver
            nota em RyanAdminProducts.css .rap-subdeck-fields-inner): um
            wrapper sempre-montado com overflow:hidden cortaria o picker de
            ícone da descrição assim que ele tentasse escapar da caixa. */}
        {isOpen && (
            <div className="rap-subdeck-section-body">
                {children}
            </div>
        )}
    </div>
);

// ── Ícone de informação com tooltip ao lado de um rótulo — mesmo padrão já
// usado em RyanSubdeckCard.tsx (.rsc-info-btn/.rsc-tooltip), reaproveitado
// aqui pra não empilhar parágrafo de texto explicativo embaixo de cada campo.
const RapInfoTip: React.FC<{ text: string }> = ({ text }) => (
    <span className="rap-info-btn">
        <Info size={12} />
        <span className="rap-tooltip">{text}</span>
    </span>
);

// ── Collapsible Section ─────────────────────────────────────────────

const Section: React.FC<{
    title: string;
    subtitle?: string;
    icon?: React.ReactNode;
    children: React.ReactNode;
    defaultOpen?: boolean;
    variant?: 'white' | 'solid';
}> = ({ title, subtitle, icon, children, defaultOpen = false, variant = 'white' }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className={`rap-section${variant === 'solid' ? ' rap-section--solid' : ''}${open ? ' rap-section--open' : ''}`}>
            <button type="button" className="rap-section-header" onClick={() => setOpen(v => !v)}>
                <span className="rap-section-label">
                    {icon && <span className="rap-section-icon">{icon}</span>}
                    <span className="rap-section-label-text">
                        {title}
                        {subtitle && <span className="rap-section-subtitle">{subtitle}</span>}
                    </span>
                </span>
                {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
            </button>
            {open && <div className="rap-section-body">{children}</div>}
        </div>
    );
};

// ── Dropdown customizado do seletor "Página de Divulgação" (Marketing) ──
// Select nativo trocado por um trigger+menu no mesmo padrão visual/de
// interação do dropdown de perfil do header (RyanHeader.tsx/.css: clique
// fora fecha via mousedown, fade+slide-in) — cabe período e nome mais
// legíveis por linha do que um <select> nativo permite.
const RapProductDropdown: React.FC<{
    products: RyanProduct[];
    value: string;
    onChange: (id: string) => void;
    placeholder: string;
}> = ({ products, value, onChange, placeholder }) => {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (rootRef.current?.contains(e.target as Node)) return;
            setOpen(false);
        };
        if (open) document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [open]);

    const selected = products.find(p => p._id === value) || null;

    return (
        <div className="rap-mkt-dropdown" ref={rootRef}>
            <button type="button" className="rap-mkt-dropdown-trigger" onClick={() => setOpen(v => !v)}>
                <Globe size={15} className="rap-mkt-dropdown-icon" />
                <span className={`rap-mkt-dropdown-value${!selected ? ' rap-mkt-dropdown-placeholder' : ''}`}>
                    {selected ? selected.title : placeholder}
                </span>
                {selected?.periodo != null && <span className="rap-mkt-dropdown-badge">{selected.periodo}°</span>}
                <ChevronDown size={15} className={`rap-mkt-dropdown-chevron${open ? ' open' : ''}`} />
            </button>
            {open && (
                <div className="rap-mkt-dropdown-menu">
                    {products.length === 0 ? (
                        <div className="rap-mkt-dropdown-empty">Nenhum produto neste período.</div>
                    ) : (
                        products.map(p => (
                            <button
                                key={p._id} type="button"
                                className={`rap-mkt-dropdown-item${p._id === value ? ' active' : ''}`}
                                onClick={() => { onChange(p._id); setOpen(false); }}
                            >
                                <span className="rap-mkt-dropdown-item-title">{p.title}</span>
                                {p.periodo != null && <span className="rap-mkt-dropdown-badge">{p.periodo}°</span>}
                                {p._id === value && <CheckCircle size={14} className="rap-mkt-dropdown-check" />}
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

// ── Format helpers ─────────────────────────────────────────────────

function fmt(cents: number) {
    return `R$ ${(cents / 100).toFixed(2).replace('.', ',')}`;
}

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════

type ProductsInnerTab = 'produtos' | 'marketing';

interface RyanAdminProductsProps {
    onHeaderChange: (title: string, subtitle: string) => void;
    // Controle externo da sub-aba (Produtos/Marketing) — usado pelo
    // RyanAdminHub, que unificou a barra de navegação e agora trata essas 2
    // posições como abas de primeiro nível, lado a lado com Acesso/Vendas/etc.
    // Campanhas deixou de ser uma sub-aba própria — virou um modo dentro de
    // Marketing (ver mktMode), reaproveitando RyanAdminCampaigns controlado
    // externamente (mode/onModeChange) em vez de uma posição isolada na barra.
    // Sem esses dois props (uso standalone, se algum dia voltar a existir),
    // o componente cai de volta no comportamento original: estado próprio +
    // sua própria barra interna de abas.
    activeTab?: ProductsInnerTab;
    onTabChange?: (tab: ProductsInnerTab) => void;
}

// ── Seletor de conteúdo do produto (venda granular) ─────────────────────
// Modal de duas colunas: à esquerda a lista de campos (por grupo, só nome +
// estado); à direita o conteúdo de UM campo por vez — o selecionado na
// esquerda — carregado sob demanda via GET /api/ryan/spaces/:spaceId (rota
// que já devolve tabs+subdecks completos pro admin, sem precisar de rota
// nova). Evita jogar abas/decks de todos os campos na tela ao mesmo tempo.
// Reaproveitado idêntico pelo form de criar e pelo de editar.
const SEM_ABA_KEY = '__no_tab__';

interface GrantPickerContent {
    tabs: { _id: string; name: string; order: number }[];
    subdecks: { _id: string; name: string; tabId: string | null }[];
}

const grantKey = (g: GrantRef) => `${g.type}:${g.refId}`;

function summarizeGrants(grants: GrantRef[]): string {
    const bySpace = grants.filter(g => g.type === 'space').length;
    const byTab = grants.filter(g => g.type === 'tab').length;
    const bySubdeck = grants.filter(g => g.type === 'subdeck').length;
    const parts: string[] = [];
    if (bySpace) parts.push(`${bySpace} campo${bySpace !== 1 ? 's' : ''} inteiro${bySpace !== 1 ? 's' : ''}`);
    if (byTab) parts.push(`${byTab} aba${byTab !== 1 ? 's' : ''}`);
    if (bySubdeck) parts.push(`${bySubdeck} deck${bySubdeck !== 1 ? 's' : ''} avulso${bySubdeck !== 1 ? 's' : ''}`);
    return parts.length > 0 ? `${parts.join(' · ')} selecionado${bySpace + byTab + bySubdeck !== 1 ? 's' : ''}` : 'Nada selecionado ainda';
}

const RyanProductGrantModal: React.FC<{
    periodKeys: string[];
    groupedSpaces: Record<string, RyanSpaceRef[]>;
    groupLabel: (key: string) => string;
    grants: GrantRef[];
    onChange: (grants: GrantRef[]) => void;
    onClose: () => void;
}> = ({ periodKeys, groupedSpaces, groupLabel, grants, onChange, onClose }) => {
    const allSpaces = useMemo(() => periodKeys.flatMap(k => groupedSpaces[k] || []), [periodKeys, groupedSpaces]);
    const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(allSpaces[0]?._id || null);
    const [contentBySpace, setContentBySpace] = useState<Record<string, GrantPickerContent>>({});
    const [loadingSpaceId, setLoadingSpaceId] = useState<string | null>(null);
    // Override manual de expandir/fechar os decks de uma aba — por padrão uma
    // aba já começa aberta só se tiver algum subdeck dela marcado (pra não
    // esconder uma seleção já feita), fechada senão, pra reduzir poluição
    // visual. Reseta a cada troca de campo.
    const [tabExpandOverride, setTabExpandOverride] = useState<Record<string, boolean>>({});

    const grantSet = useMemo(() => new Set(grants.map(grantKey)), [grants]);

    const toggleGrant = (g: GrantRef) => {
        const key = grantKey(g);
        onChange(grantSet.has(key) ? grants.filter(x => grantKey(x) !== key) : [...grants, g]);
    };

    const loadSpace = useCallback(async (spaceId: string) => {
        setLoadingSpaceId(spaceId);
        try {
            const res = await fetch(`/api/ryan/spaces/${spaceId}`, { headers: { Authorization: `Bearer ${tok()}` } });
            if (res.ok) {
                const data = await res.json();
                setContentBySpace(prev => ({
                    ...prev,
                    [spaceId]: {
                        tabs: [...(data.tabs || [])].sort((a, b) => a.order - b.order),
                        subdecks: data.subdecks || [],
                    },
                }));
            }
        } finally {
            setLoadingSpaceId(null);
        }
    }, []);

    useEffect(() => {
        if (selectedSpaceId && !contentBySpace[selectedSpaceId]) loadSpace(selectedSpaceId);
        setTabExpandOverride({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSpaceId]);

    const selectedSpace = allSpaces.find(s => s._id === selectedSpaceId) || null;
    const content = selectedSpaceId ? contentBySpace[selectedSpaceId] : undefined;
    const spaceGranted = selectedSpaceId ? grantSet.has(`space:${selectedSpaceId}`) : false;

    const sections = content ? (() => {
        const subdecksByTab = new Map<string, GrantPickerContent['subdecks']>();
        for (const sd of content.subdecks) {
            const k = sd.tabId || SEM_ABA_KEY;
            if (!subdecksByTab.has(k)) subdecksByTab.set(k, []);
            subdecksByTab.get(k)!.push(sd);
        }
        return [
            ...content.tabs.map(t => ({ id: t._id, name: t.name, isTab: true })),
            { id: SEM_ABA_KEY, name: 'Sem aba', isTab: false },
        ]
            .map(sec => ({ ...sec, subdecks: subdecksByTab.get(sec.id) || [] }))
            .filter(sec => sec.isTab || sec.subdecks.length > 0);
    })() : [];

    // Indicador "tem algo marcado" na lista da esquerda: além do campo
    // inteiro (sempre sabido de cara), só sabemos de aba/subdeck avulso pra
    // campos já visitados nesta sessão do modal (contentBySpace) — visitar
    // o campo é o preço de mostrar o indicador certo sem buscar tudo de uma vez.
    const hasPartialGrant = (spaceId: string) => {
        const c = contentBySpace[spaceId];
        if (!c) return false;
        return grants.some(g =>
            (g.type === 'tab' && c.tabs.some(t => t._id === g.refId)) ||
            (g.type === 'subdeck' && c.subdecks.some(sd => sd._id === g.refId))
        );
    };

    return ReactDOM.createPortal(
        <div className="rap-modal-overlay rap-grant-modal-overlay" onClick={onClose}>
            <div className="rap-modal rap-modal--lg rap-grant-modal" onClick={e => e.stopPropagation()}>
                <div className="rap-modal-header">
                    <h3 className="rap-modal-title">Selecionar conteúdo do produto</h3>
                    <button className="rap-form-close" onClick={onClose}><X size={18} /></button>
                </div>

                <div className="rap-grant-modal-body">
                    <div className="rap-grant-modal-list">
                        {periodKeys.map(key => (
                            <div key={key} className="rap-grant-modal-group">
                                <span className="rap-grant-modal-group-label">{groupLabel(key)}</span>
                                {(groupedSpaces[key] || []).map(sp => {
                                    const granted = grantSet.has(`space:${sp._id}`);
                                    return (
                                        <button
                                            key={sp._id}
                                            type="button"
                                            className={`rap-grant-modal-item${selectedSpaceId === sp._id ? ' active' : ''}${granted ? ' granted' : ''}`}
                                            onClick={() => setSelectedSpaceId(sp._id)}
                                        >
                                            <span className="rap-grant-modal-item-name">{sp.name}</span>
                                            {(granted || hasPartialGrant(sp._id)) && (
                                                <CheckCircle size={14} className="rap-grant-modal-item-check" />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        ))}
                    </div>

                    <div className="rap-grant-modal-detail">
                        {!selectedSpace && <p className="rap-grant-loading">Selecione um campo à esquerda.</p>}
                        {selectedSpace && (
                            <>
                                <div className="rap-grant-space-header">
                                    <label className="rap-grant-check rap-grant-check--space">
                                        <input
                                            type="checkbox"
                                            checked={spaceGranted}
                                            onChange={() => toggleGrant({ type: 'space', refId: selectedSpace._id })}
                                        />
                                        <strong>{selectedSpace.name} (campo inteiro)</strong>
                                    </label>
                                </div>

                                {loadingSpaceId === selectedSpaceId && <p className="rap-grant-loading">Carregando…</p>}
                                {content && sections.length === 0 && (
                                    <p className="rap-grant-loading">Nenhuma aba ou deck neste campo.</p>
                                )}
                                {content && !spaceGranted && sections.length > 0 && (
                                    <div className="rap-grant-space-content">
                                        {sections.map(sec => {
                                            const tabGranted = sec.isTab && grantSet.has(`tab:${sec.id}`);
                                            const hasSelection = sec.subdecks.some(sd => grantSet.has(`subdeck:${sd._id}`));
                                            const isOpen = sec.id in tabExpandOverride ? tabExpandOverride[sec.id] : hasSelection;
                                            return (
                                                <div key={sec.id} className="rap-grant-tab-block">
                                                    <div className="rap-grant-tab-header">
                                                        {sec.isTab ? (
                                                            <label className="rap-grant-check rap-grant-check--tab">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={!!tabGranted}
                                                                    onChange={() => toggleGrant({ type: 'tab', refId: sec.id })}
                                                                />
                                                                {sec.name}
                                                            </label>
                                                        ) : (
                                                            sec.subdecks.length > 0 && (
                                                                <span className="rap-grant-tab-label-static">{sec.name}</span>
                                                            )
                                                        )}
                                                        {sec.subdecks.length > 0 && (
                                                            <button
                                                                type="button"
                                                                className="rap-grant-expand-btn"
                                                                onClick={() => setTabExpandOverride(prev => ({ ...prev, [sec.id]: !isOpen }))}
                                                                title={isOpen ? 'Fechar decks' : 'Ver decks avulsos'}
                                                            >
                                                                {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                                            </button>
                                                        )}
                                                    </div>
                                                    {isOpen && sec.subdecks.length > 0 && (
                                                        <div className="rap-grant-subdeck-list">
                                                            {sec.subdecks.map(sd => {
                                                                const subGranted = grantSet.has(`subdeck:${sd._id}`);
                                                                const covered = !!tabGranted;
                                                                return (
                                                                    <label key={sd._id} className="rap-grant-check rap-grant-check--subdeck">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={subGranted || covered}
                                                                            disabled={covered}
                                                                            onChange={() => toggleGrant({ type: 'subdeck', refId: sd._id })}
                                                                        />
                                                                        {sd.name}
                                                                    </label>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                {spaceGranted && (
                                    <p className="rap-grant-loading">Campo inteiro selecionado — abas e decks deste campo já estão liberados.</p>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div className="rap-modal-footer">
                    <span className="rap-grant-summary">{summarizeGrants(grants)}</span>
                    <button className="rap-btn-primary" onClick={onClose}>Concluído</button>
                </div>
            </div>
        </div>,
        document.body
    );
};

const RyanAdminProducts: React.FC<RyanAdminProductsProps> = ({ onHeaderChange, activeTab: externalActiveTab, onTabChange }) => {
    const [internalActiveTab, setInternalActiveTab] = useState<ProductsInnerTab>('produtos');
    const isExternallyControlled = externalActiveTab !== undefined;
    const activeTab = isExternallyControlled ? externalActiveTab : internalActiveTab;
    const setActiveTab = onTabChange ?? setInternalActiveTab;

    useEffect(() => {
        // Continua sendo esta a fonte única do título/subtítulo dessas 3 abas
        // (evita duplicar o mapa em dois lugares) — dispara igual estando
        // controlado externamente pelo RyanAdminHub ou standalone.
        const headers: Record<ProductsInnerTab, [string, string]> = {
            produtos:   ['Produtos', 'Crie e gerencie os produtos da plataforma.'],
            marketing:  ['Marketing', 'Configure a página pública de venda de cada produto e crie campanhas.'],
        };
        const [title, subtitle] = headers[activeTab];
        onHeaderChange(title, subtitle);
    }, [activeTab, onHeaderChange]);
    const [spaces,   setSpaces]   = useState<RyanSpaceRef[]>([]);
    const [spaceGroups, setSpaceGroups] = useState<RyanSpaceGroupRef[]>([]);
    const [products, setProducts] = useState<RyanProduct[]>([]);
    const [loading,  setLoading]  = useState(true);

    // ── Create form state (always visible) ──
    const [createForm, setCreateForm] = useState({
        title: '',
        grants: [] as GrantRef[],
        priceCents: 0,
        accessExpiresAt: '', // yyyy-mm-dd de <input type="date">, vazio = sem expiração
        institutionTags: '', // texto livre, "afya, outra" -> split por vírgula no submit
    });
    const [creating, setCreating] = useState(false);

    // ── Modal do seletor de conteúdo (venda granular) — qual form ele edita ──
    const [grantModalTarget, setGrantModalTarget] = useState<'create' | 'edit' | null>(null);

    // ── Edit modal state ──
    const [editOpen,   setEditOpen]   = useState(false);
    const [editingId,  setEditingId]  = useState<string | null>(null);
    const [editForm,   setEditForm]   = useState({
        title: '',
        grants: [] as GrantRef[],
        priceCents: 0,
        active: true,
        accessExpiresAt: '', // yyyy-mm-dd de <input type="date">, vazio = sem expiração
        institutionTags: '', // texto livre, "afya, outra" -> split por vírgula no submit
    });
    const [saving,        setSaving]        = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(false);
    const [deleting,      setDeleting]      = useState(false);

    // ── Aplicar accessExpiresAt a quem já comprou ──
    const [applyExpiryConfirm, setApplyExpiryConfirm] = useState(false);
    const [applyingExpiry, setApplyingExpiry] = useState(false);
    const [applyExpiryResult, setApplyExpiryResult] = useState<string | null>(null);

    // ── Marketing tab state ──
    const [mktProductId, setMktProductId] = useState('');
    const [mktPeriodFilter, setMktPeriodFilter] = useState<number | 'all'>('all');
    // Modo do corpo da aba Marketing — os 3 controles do topo (seletor de
    // produto + Criar campanha + Ver campanhas) ficam sempre visíveis
    // independente do modo; só o corpo abaixo alterna. Campanhas deixou de
    // ser uma sub-aba própria (RyanAdminHub/PRODUCTS_SUBTABS) e virou isto.
    const [mktMode, setMktMode] = useState<'produto' | 'criar-campanha' | 'ver-campanhas'>('produto');
    // Incrementado a cada clique em "Criar campanha" — sinaliza pro
    // RyanAdminCampaigns resetar pra um formulário em branco mesmo se o
    // último estado interno dele era de edição (senão reabriria com os
    // dados da última campanha editada, já que list/form aqui é controlado
    // externamente e o componente não sabe distinguir "abrir em branco" de
    // "abrir pra editar" só pelo mode mudar de 'list' pra 'form').
    const [mktCampaignCreateSignal, setMktCampaignCreateSignal] = useState(0);
    const [mktSlug, setMktSlug]     = useState('');
    const [mktForm, setMktForm]     = useState<PublicPage>(defaultPublicPage());
    const [contentStruct, setContentStruct] = useState<SpaceContent[]>([]);
    const [loadingContent, setLoadingContent] = useState(false);
    const [savingMkt, setSavingMkt] = useState(false);
    const [mktSaved, setMktSaved]   = useState(false);
    const [statCardIconPicker, setStatCardIconPicker] = useState<number | null>(null);
    const [expandedBlockId, setExpandedBlockId] = useState<string | null>(null);
    const [addBlockPickerOpen, setAddBlockPickerOpen] = useState(false);
    const [blockDeleteConfirm, setBlockDeleteConfirm] = useState<string | null>(null);
    const [expandedDescIds, setExpandedDescIds] = useState<Set<string>>(new Set());
    const toggleDescExpanded = (id: string) => setExpandedDescIds(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });
    // Dropdown por subdeck (link + descrição escondidos até o admin clicar) e
    // por seção (aba, ou Apostilas/Flashcards) — tudo fechado por padrão, pra
    // não abrir a aba de marketing já poluída com dezenas de cards expandidos.
    const [expandedSubdecks, setExpandedSubdecks] = useState<Set<string>>(new Set());
    const toggleSubdeckExpanded = (id: string) => setExpandedSubdecks(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });
    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
    const toggleSectionExpanded = (key: string) => setExpandedSections(prev => {
        const next = new Set(prev);
        next.has(key) ? next.delete(key) : next.add(key);
        return next;
    });
    const descPreview = (raw: string): string => {
        if (!raw) return '';
        const blocks = parseDescBlocks(raw);
        const text = blocks.map(b => b.text).filter(Boolean).join(' ');
        return text.length > 70 ? `${text.slice(0, 70)}…` : text;
    };
    const blockSensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );
    const subdeckSensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );
    // Modal "Link de prévia" — substitui o input sempre visível (poucos
    // subdecks recebem link; um input por card ocupava espaço à toa e ainda
    // empurrava o grid — ver nota em RyanAdminProducts.css .rap-subdeck-grid).
    const [previewLinkModalId, setPreviewLinkModalId] = useState<string | null>(null);
    const [previewLinkDraft, setPreviewLinkDraft] = useState('');
    const openPreviewLinkModal = (id: string) => {
        setPreviewLinkModalId(id);
        setPreviewLinkDraft(mktForm.subdeckPreviewLinks[id] || '');
    };
    const savePreviewLinkModal = () => {
        if (!previewLinkModalId) return;
        setMkt({ subdeckPreviewLinks: { ...mktForm.subdeckPreviewLinks, [previewLinkModalId]: previewLinkDraft.trim() } });
        setPreviewLinkModalId(null);
    };

    // Modal "Ícone da aba" — só aparece no modo "Espelhar abas"; ícone é
    // opcional (RyanSubdeckIconPicker já tem o próprio botão "Remover").
    const [tabIconModalId, setTabIconModalId] = useState<string | null>(null);

    // ── Modo "Personalizado" (contentOrganization: 'custom') — seções livres
    // com nome escolhido pelo admin, pra agrupar qualquer item (de qualquer
    // campo/aba incluído no produto) sob um bloco só. Reorder é por seta
    // (↑/↓) em vez de drag — evita a complexidade de um DnD cruzando vários
    // containers só pra mover uma seção de posição. ──
    const [newSectionName, setNewSectionName] = useState('');
    const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
    // Modal "Ícone da seção" — mesmo padrão do tabIconModalId acima, só que
    // pras seções do modo "Personalizado" (namespace separado: ids de seção
    // são gerados no cliente via genId(), nunca colidem com ObjectIds de aba).
    const [sectionIconModalId, setSectionIconModalId] = useState<string | null>(null);

    const addCustomSection = () => {
        const name = newSectionName.trim();
        if (!name) return;
        const maxOrder = mktForm.customSections.reduce((m, s) => Math.max(m, s.order), -1);
        setMkt({ customSections: [...mktForm.customSections, { id: genId(), name, icon: null, description: '', order: maxOrder + 1 }] });
        setNewSectionName('');
    };

    const renameCustomSection = (id: string, name: string) => {
        setMkt({ customSections: mktForm.customSections.map(s => s.id === id ? { ...s, name } : s) });
    };

    const setCustomSectionDescription = (id: string, description: string) => {
        setMkt({ customSections: mktForm.customSections.map(s => s.id === id ? { ...s, description } : s) });
    };

    const deleteCustomSection = (id: string) => {
        setMkt({
            customSections: mktForm.customSections.filter(s => s.id !== id),
            customSectionAssignments: Object.fromEntries(
                Object.entries(mktForm.customSectionAssignments).filter(([, sectionId]) => sectionId !== id)
            ),
        });
    };

    const moveCustomSection = (id: string, dir: -1 | 1) => {
        const sorted = [...mktForm.customSections].sort((a, b) => a.order - b.order);
        const idx = sorted.findIndex(s => s.id === id);
        const swapIdx = idx + dir;
        if (idx === -1 || swapIdx < 0 || swapIdx >= sorted.length) return;
        const a = sorted[idx], b = sorted[swapIdx];
        setMkt({
            customSections: mktForm.customSections.map(s => {
                if (s.id === a.id) return { ...s, order: b.order };
                if (s.id === b.id) return { ...s, order: a.order };
                return s;
            }),
        });
    };

    const assignSubdeckToSection = (subdeckId: string, sectionId: string) => {
        const next = { ...mktForm.customSectionAssignments };
        if (sectionId) next[subdeckId] = sectionId;
        else delete next[subdeckId];
        setMkt({ customSectionAssignments: next });
    };

    // ── Load data ──
    useEffect(() => {
        const h = { Authorization: `Bearer ${tok()}` };
        Promise.all([
            fetch('/api/ryan/admin/spaces', { headers: h }).then(r => r.ok ? r.json() : { spaces: [] }),
            fetch('/api/ryan/admin/space-groups', { headers: h }).then(r => r.ok ? r.json() : []),
            fetch('/api/ryan/admin/products', { headers: h }).then(r => r.ok ? r.json() : []),
        ]).then(([sp, gr, pr]) => {
            setSpaces(Array.isArray(sp) ? sp : sp.spaces || []);
            setSpaceGroups(Array.isArray(gr) ? gr : []);
            setProducts(Array.isArray(pr) ? pr : []);
        }).finally(() => setLoading(false));
    }, []);

    const NO_GROUP_KEY = '__none';

    // ── Grouped spaces by RyanSpaceGroup ──
    const groupedSpaces = useMemo(() => {
        const groups: Record<string, RyanSpaceRef[]> = {};
        spaces.forEach(sp => {
            const key = sp.groupId || NO_GROUP_KEY;
            if (!groups[key]) groups[key] = [];
            groups[key].push(sp);
        });
        return groups;
    }, [spaces]);

    const periodKeys = useMemo(() => {
        return [
            ...spaceGroups.map(g => g._id),
            NO_GROUP_KEY,
        ].filter(key => groupedSpaces[key]?.length);
    }, [spaceGroups, groupedSpaces]);

    const groupLabel = (key: string) => key === NO_GROUP_KEY ? 'Sem Grupo' : (spaceGroups.find(g => g._id === key)?.name || key);

    // ── Períodos de produto — pro filtro do seletor da aba Marketing (produtos
    // homônimos em períodos diferentes, ex: "OSCE" do 2° e do 3°, ficam fáceis
    // de distinguir). ──
    const mktProductPeriods = useMemo(() => {
        return Array.from(new Set(products.map(p => p.periodo).filter((p): p is number => p != null)))
            .sort((a, b) => a - b);
    }, [products]);

    const mktFilteredProducts = useMemo(() => {
        return mktPeriodFilter === 'all' ? products : products.filter(p => p.periodo === mktPeriodFilter);
    }, [products, mktPeriodFilter]);

    // ── Tags de instituição já usadas no sistema (sugestões do datalist) ──
    // União de RyanProduct.grantsInstitutionTags (todos os produtos) e
    // RyanSpaceGroup.requiredInstitutionTag (todos os grupos) — não existe
    // uma coleção própria de tags, então a lista de sugestões é derivada do
    // que já está em uso, sem rota nova (products/spaceGroups já carregados).
    const knownInstitutionTags = useMemo(() => {
        const tags = new Set<string>();
        products.forEach(p => (p.grantsInstitutionTags || []).forEach(t => t && tags.add(t)));
        spaceGroups.forEach(g => { if (g.requiredInstitutionTag) tags.add(g.requiredInstitutionTag); });
        return [...tags].sort();
    }, [products, spaceGroups]);

    // ── Load content structure when marketing product changes ──
    useEffect(() => {
        if (!mktProductId) { setContentStruct([]); return; }
        const product = products.find(p => p._id === mktProductId);
        if (product) {
            setMktSlug(product.publicSlug || '');
            const pg = product.publicPage || defaultPublicPage();
            // showPrice era um único campo pros dois blocos (produtos salvos
            // antes da divisão Hero/Adquira agora) — herda o valor legado nos
            // dois novos campos quando eles ainda não foram salvos.
            const legacyShowPrice = (pg as unknown as { showPrice?: boolean }).showPrice;
            setMktForm({
                ...defaultPublicPage(),
                ...pg,
                showPriceHero: pg.showPriceHero ?? legacyShowPrice ?? true,
                showPriceClose: pg.showPriceClose ?? legacyShowPrice ?? true,
                // 'auto' é valor legado (produtos salvos antes da divisão abas/categorias) — trata como 'tabs'.
                contentOrganization: (pg.contentOrganization as string) === 'auto'
                    ? 'tabs'
                    : (pg.contentOrganization || 'tabs'),
                differentialStatCards: pg.differentialStatCards?.length
                    ? pg.differentialStatCards
                    : defaultPublicPage().differentialStatCards,
                osceScenario: { ...defaultPublicPage().osceScenario, ...(pg.osceScenario || {}) },
                contentBlocks: (pg.contentBlocks || []).map(b => ({
                    ...b,
                    backgroundVariant: b.backgroundVariant || 'white',
                    data: b.type === 'apostila_demo'
                        ? { ...defaultApostilaDemoData(), ...(b.data as Partial<ApostilaDemoData>) }
                        : b.type === 'flashcards_demo'
                        ? { ...defaultFlashcardsDemoData(), ...(b.data as Partial<FlashcardsDemoData>) }
                        : b.type === 'metodo_demo'
                        ? { ...defaultMetodoDemoData(), ...(b.data as Partial<MetodoDemoData>) }
                        : b.type === 'estudo_ativo_demo'
                        ? { ...defaultEstudoAtivoDemoData(), ...(b.data as Partial<EstudoAtivoDemoData>) }
                        : { ...defaultTutoriaDemoData(), ...(b.data as Partial<TutoriaDemoData>) },
                })),
                subdeckDescriptions: pg.subdeckDescriptions || {},
                subdeckPreviewLinks: pg.subdeckPreviewLinks || {},
                subdeckOrder: pg.subdeckOrder || {},
                tabIcons: pg.tabIcons || {},
                customSections: pg.customSections || [],
                customSectionAssignments: pg.customSectionAssignments || {},
            });
        }
        setLoadingContent(true);
        fetch(`/api/ryan/admin/products/${mktProductId}/content-structure`, {
            headers: { Authorization: `Bearer ${tok()}` },
        }).then(r => r.ok ? r.json() : { spaces: [] })
            .then(d => setContentStruct(d.spaces || []))
            .finally(() => setLoadingContent(false));
    }, [mktProductId]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Create handlers ──

    // "afya, outra" -> ['afya', 'outra'] — texto livre por enquanto, só
    // existe uma tag ('afya') hoje, não precisa de um seletor.
    const parseTagsInput = (s: string): string[] => s.split(',').map(t => t.trim()).filter(Boolean);

    const handleCreate = async () => {
        if (!createForm.title.trim()) return;
        setCreating(true);
        try {
            const res = await fetch('/api/ryan/admin/products', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
                body: JSON.stringify({
                    title: createForm.title.trim(),
                    grants: createForm.grants,
                    priceCents: createForm.priceCents,
                    accessExpiresAt: createForm.accessExpiresAt || null,
                    grantsInstitutionTags: parseTagsInput(createForm.institutionTags),
                }),
            });
            if (!res.ok) throw new Error(await res.text());
            const created = await res.json();
            setProducts(prev => [{ ...created, userCount: 0 }, ...prev]);
            setCreateForm({ title: '', grants: [], priceCents: 0, accessExpiresAt: '', institutionTags: '' });
        } finally {
            setCreating(false);
        }
    };

    // ── Edit handlers ──

    // RyanProduct.accessExpiresAt vem como string ISO (ou null) do backend —
    // <input type="date"> espera exatamente "yyyy-mm-dd".
    const toDateInputValue = (iso: string | null): string => iso ? iso.slice(0, 10) : '';

    const openEdit = (p: RyanProduct) => {
        setEditingId(p._id);
        setEditForm({
            title: p.title,
            grants: p.grants || [],
            priceCents: p.priceCents || 0,
            active: p.active,
            accessExpiresAt: toDateInputValue(p.accessExpiresAt),
            institutionTags: (p.grantsInstitutionTags || []).join(', '),
        });
        setDeleteConfirm(false);
        setApplyExpiryConfirm(false);
        setApplyExpiryResult(null);
        setEditOpen(true);
    };

    const closeEdit = () => {
        setEditOpen(false);
        setEditingId(null);
        setDeleteConfirm(false);
        setApplyExpiryConfirm(false);
        setApplyExpiryResult(null);
    };

    const handleApplyExpiryToExisting = async () => {
        if (!editingId) return;
        setApplyingExpiry(true);
        setApplyExpiryResult(null);
        try {
            const res = await fetch(`/api/ryan/admin/products/${editingId}/apply-access-expiry`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${tok()}` },
            });
            const d = await res.json();
            if (!res.ok) {
                setApplyExpiryResult(d.message || 'Erro ao aplicar a data.');
                return;
            }
            setApplyExpiryResult(
                d.entriesUpdated > 0
                    ? `Aplicado a ${d.usersUpdated} usuário${d.usersUpdated !== 1 ? 's' : ''} (${d.entriesUpdated} de ${d.entriesFound} entrada${d.entriesFound !== 1 ? 's' : ''} sem data). ${d.entriesFound - d.entriesUpdated > 0 ? `${d.entriesFound - d.entriesUpdated} já tinham uma data definida e foram preservadas.` : ''}`
                    : `Nenhuma entrada alterada — de ${d.entriesFound} encontrada${d.entriesFound !== 1 ? 's' : ''}, todas já tinham uma data definida.`
            );
        } catch {
            setApplyExpiryResult('Erro de conexão.');
        } finally {
            setApplyingExpiry(false);
            setApplyExpiryConfirm(false);
        }
    };

    const handleEdit = async () => {
        if (!editingId || !editForm.title.trim()) return;
        setSaving(true);
        try {
            const res = await fetch(`/api/ryan/admin/products/${editingId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
                body: JSON.stringify({
                    title: editForm.title.trim(),
                    grants: editForm.grants,
                    priceCents: editForm.priceCents,
                    active: editForm.active,
                    accessExpiresAt: editForm.accessExpiresAt || null,
                    grantsInstitutionTags: parseTagsInput(editForm.institutionTags),
                }),
            });
            if (!res.ok) throw new Error(await res.text());
            const updated = await res.json();
            setProducts(prev => prev.map(p => p._id === editingId ? updated : p));
            closeEdit();
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = useCallback(async (id: string) => {
        setDeleting(true);
        try {
            await fetch(`/api/ryan/admin/products/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${tok()}` },
            });
            setProducts(prev => prev.filter(p => p._id !== id));
        } finally {
            setDeleting(false);
        }
    }, []);

    const handleToggleActive = async (p: RyanProduct) => {
        const res = await fetch(`/api/ryan/admin/products/${p._id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
            body: JSON.stringify({ active: !p.active }),
        });
        if (res.ok) {
            const updated = await res.json();
            setProducts(prev => prev.map(x => x._id === p._id ? updated : x));
        }
    };

    // ── Marketing tab handlers ──

    const setMkt = (patch: Partial<PublicPage>) => setMktForm(f => ({ ...f, ...patch }));

    const setStatCard = (i: number, patch: Partial<{ icon: string; value: string; label: string }>) => {
        const cards = [...mktForm.differentialStatCards];
        cards[i] = { ...cards[i], ...patch };
        setMkt({ differentialStatCards: cards });
    };

    // ── Blocos de demonstração ──

    const addBlock = (type: ContentBlock['type']) => {
        const sorted = [...mktForm.contentBlocks].sort((a, b) => a.order - b.order);
        const lastVariant = sorted.length > 0 ? sorted[sorted.length - 1].backgroundVariant : 'green';
        const newBlock: ContentBlock = {
            id: genId(),
            type,
            enabled: true,
            order: mktForm.contentBlocks.length,
            backgroundVariant: lastVariant === 'white' ? 'green' : 'white',
            data: type === 'apostila_demo' ? defaultApostilaDemoData()
                : type === 'flashcards_demo' ? defaultFlashcardsDemoData()
                : type === 'metodo_demo' ? defaultMetodoDemoData()
                : type === 'estudo_ativo_demo' ? defaultEstudoAtivoDemoData()
                : type === 'tutoria_demo' ? defaultTutoriaDemoData()
                : defaultVideoDemoData(),
        };
        setMkt({ contentBlocks: [...mktForm.contentBlocks, newBlock] });
        setExpandedBlockId(newBlock.id);
        setAddBlockPickerOpen(false);
    };

    const updateBlock = (id: string, patch: Partial<ContentBlock>) => {
        setMkt({ contentBlocks: mktForm.contentBlocks.map(b => b.id === id ? { ...b, ...patch } : b) });
    };

    const updateBlockData = (id: string, dataPatch: Partial<ApostilaDemoData> | Partial<FlashcardsDemoData> | Partial<MetodoDemoData> | Partial<EstudoAtivoDemoData> | Partial<TutoriaDemoData> | Partial<VideoDemoData>) => {
        setMkt({
            // O caller (SortableBlockItem) só passa um dataPatch compatível com o
            // TIPO do bloco correspondente — TS não consegue correlacionar isso
            // sozinho num spread genérico entre uniões, daí a asserção.
            contentBlocks: mktForm.contentBlocks.map(b => b.id === id ? { ...b, data: { ...b.data, ...dataPatch } as ContentBlock['data'] } : b),
        });
    };

    const removeBlock = (id: string) => {
        setMkt({ contentBlocks: mktForm.contentBlocks.filter(b => b.id !== id).map((b, i) => ({ ...b, order: i })) });
        setBlockDeleteConfirm(null);
        if (expandedBlockId === id) setExpandedBlockId(null);
    };

    const handleBlockDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const items = [...mktForm.contentBlocks].sort((a, b) => a.order - b.order);
        const oldIndex = items.findIndex(b => b.id === active.id);
        const newIndex = items.findIndex(b => b.id === over.id);
        const reordered = arrayMove(items, oldIndex, newIndex).map((b, i) => ({ ...b, order: i }));
        setMkt({ contentBlocks: reordered });
    };

    // Reordena só DENTRO do grupo (Apostilas OU Flashcards) arrastado — a
    // ordem salva (subdeckOrder) só é comparável entre itens do mesmo tipo,
    // então recalcular só esse subconjunto (0..n-1) e mesclar no mapa
    // existente nunca invalida a ordem já salva do outro grupo.
    const handleSubdeckDragEnd = (groupItems: SubdeckItem[]) => (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = groupItems.findIndex(sd => sd._id === active.id);
        const newIndex = groupItems.findIndex(sd => sd._id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;
        const reordered = arrayMove(groupItems, oldIndex, newIndex);
        const patch: Record<string, number> = {};
        reordered.forEach((sd, i) => { patch[sd._id] = i; });
        setMkt({ subdeckOrder: { ...mktForm.subdeckOrder, ...patch } });
    };

    const handleSaveMarketing = async () => {
        if (!mktProductId) return;
        setSavingMkt(true);
        try {
            const res = await fetch(`/api/ryan/admin/products/${mktProductId}/public-page`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
                body: JSON.stringify({ ...mktForm, publicSlug: mktSlug }),
            });
            if (res.ok) {
                const updated = await res.json();
                setProducts(prev => prev.map(p => p._id === mktProductId ? updated : p));
                setMktSaved(true);
                setTimeout(() => setMktSaved(false), 2000);
            }
        } finally {
            setSavingMkt(false);
        }
    };

    const allSubdecks: SubdeckItem[] = contentStruct.flatMap(sp =>
        sp.subdecks.map(sd => ({ ...sd, spaceName: sp.name }))
    );

    // Abas deduplicadas entre todos os campos do produto (produto combo pode
    // ter mais de um Space, cada um com suas próprias abas) — usada só pra
    // agrupar o editor de conteúdo por aba quando contentOrganization é 'tabs'.
    const allTabs: Array<{ _id: string; name: string }> = [];
    contentStruct.forEach(sp => (sp.tabs || []).forEach(t => {
        if (!allTabs.some(existing => existing._id === t._id)) allTabs.push(t);
    }));

    const mktProduct = products.find(p => p._id === mktProductId);

    if (loading) {
        return <div className="rap-spinner-wrap"><div className="rap-spinner" /></div>;
    }

    return (
        <>
        <div className={`rap-root${isExternallyControlled ? ' rap-root--embedded' : ''}`}>

            {/* ── Tabs ── */}
            {/* Só renderiza a barra interna no uso standalone — controlado pelo
                RyanAdminHub, Produtos/Marketing já são 2 posições da barra
                unificada externa, então esta ficaria duplicada. */}
            {!isExternallyControlled && (
                <div className="rap-tabs">
                    <button
                        className={`rap-tab${activeTab === 'produtos' ? ' active' : ''}`}
                        onClick={() => setActiveTab('produtos')}
                    >
                        <Package size={15} /> Produtos
                    </button>
                    <button
                        className={`rap-tab${activeTab === 'marketing' ? ' active' : ''}`}
                        onClick={() => setActiveTab('marketing')}
                    >
                        <Globe size={15} /> Marketing
                    </button>
                </div>
            )}

            {/* ══════════════════════════════════════════ */}
            {/* ABA PRODUTOS                              */}
            {/* ══════════════════════════════════════════ */}
            {activeTab === 'produtos' && (
                <div className="rap-tab-content">

                    {/* Sugestões do campo "Tags de instituição" (create + edit) — combobox
                        nativo: aceita escolher uma tag já usada no sistema ou digitar uma
                        nova, sem travar num enum fixo. */}
                    <datalist id="rap-institution-tags">
                        {knownInstitutionTags.map(tag => <option key={tag} value={tag} />)}
                    </datalist>

                    {/* ── Create form — sempre visível ── */}
                    <div className="rap-create-card">
                        <div className="rap-create-top">
                            <div className="rap-field rap-field-grow">
                                <label className="rap-label">Título do produto</label>
                                <input
                                    className="rap-input"
                                    value={createForm.title}
                                    onChange={e => setCreateForm(f => ({ ...f, title: e.target.value }))}
                                    placeholder="Nome do produto..."
                                />
                            </div>
                        </div>

                        <div className="rap-create-spaces">
                            <label className="rap-label">Conteúdo do produto</label>
                            <div className="rap-grant-trigger-row">
                                <button type="button" className="rap-btn-ghost" onClick={() => setGrantModalTarget('create')}>
                                    <Layers size={15} /> Selecionar conteúdo
                                </button>
                                <span className="rap-grant-summary-inline">{summarizeGrants(createForm.grants)}</span>
                            </div>
                        </div>

                        <div className="rap-create-footer">
                            <div className="rap-field">
                                <label className="rap-label">Preço (centavos)</label>
                                <input className="rap-input rap-input-price" type="number" min={0}
                                    value={createForm.priceCents}
                                    onChange={e => setCreateForm(f => ({ ...f, priceCents: Number(e.target.value) }))}
                                    placeholder="ex: 19000 = R$190,00" />
                                {createForm.priceCents > 0 && (
                                    <span className="rap-price-preview">{fmt(createForm.priceCents)}</span>
                                )}
                            </div>
                            <div className="rap-field">
                                <label className="rap-label">Expiração do acesso (opcional)</label>
                                <input className="rap-input" type="date"
                                    value={createForm.accessExpiresAt}
                                    onChange={e => setCreateForm(f => ({ ...f, accessExpiresAt: e.target.value }))} />
                            </div>
                            <div className="rap-field">
                                <label className="rap-label">Tags de instituição (opcional)</label>
                                <input className="rap-input" type="text" list="rap-institution-tags"
                                    value={createForm.institutionTags}
                                    onChange={e => setCreateForm(f => ({ ...f, institutionTags: e.target.value }))}
                                    placeholder="ex: Afya" />
                            </div>
                            <button className="rap-btn-primary rap-btn-create" onClick={handleCreate}
                                disabled={creating || !createForm.title.trim()}>
                                {creating ? 'Criando…' : <><Plus size={14} /> Criar produto</>}
                            </button>
                        </div>
                    </div>

                    {/* ── Product Grid ── */}
                    {products.length > 0 && (
                        <div className="rap-grid-heading">
                            <span className="rap-grid-heading-text">Produtos cadastrados</span>
                            <span className="rap-grid-count">{products.length}</span>
                        </div>
                    )}
                    {products.length === 0 ? (
                        <div className="rap-empty">
                            <Package size={40} color="#0D3B0E" style={{ opacity: 0.2 }} />
                            <p>Nenhum produto criado ainda.</p>
                        </div>
                    ) : (
                        <div className="rap-product-grid">
                            {products.map(p => (
                                <div key={p._id} className={`rap-product-card${!p.active ? ' inactive' : ''}`}>
                                    <div className="rap-product-title">{p.title}</div>
                                    <div className="rap-product-badges">
                                        {(p.grants || []).filter(g => g.type === 'space').map(g => {
                                            const sp = spaces.find(s => s._id === g.refId);
                                            return (
                                                <span key={g.refId} className="rap-space-badge">
                                                    {sp?.name || 'Campo'}
                                                </span>
                                            );
                                        })}
                                        {(() => {
                                            const tabCount = (p.grants || []).filter(g => g.type === 'tab').length;
                                            const subdeckCount = (p.grants || []).filter(g => g.type === 'subdeck').length;
                                            return (
                                                <>
                                                    {tabCount > 0 && (
                                                        <span className="rap-space-badge rap-space-badge--granular">
                                                            {tabCount} aba{tabCount !== 1 ? 's' : ''}
                                                        </span>
                                                    )}
                                                    {subdeckCount > 0 && (
                                                        <span className="rap-space-badge rap-space-badge--granular">
                                                            {subdeckCount} deck{subdeckCount !== 1 ? 's' : ''} avulso{subdeckCount !== 1 ? 's' : ''}
                                                        </span>
                                                    )}
                                                </>
                                            );
                                        })()}
                                        {(p.grantsInstitutionTags || []).map(tag => (
                                            <span key={tag} className="rap-space-badge rap-space-badge--institution">
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                    <div className="rap-product-stats">
                                        <span className="rap-user-count">
                                            <Users size={12} /> {p.userCount ?? 0} usuários
                                        </span>
                                        {p.priceCents > 0 && (
                                            <span className="rap-price-chip">{fmt(p.priceCents)}</span>
                                        )}
                                    </div>
                                    <div className="rap-product-actions">
                                        {p.publicSlug && p.publicPage?.enabled && (
                                            <a href={`/ryan/produto/${p.publicSlug}`} target="_blank"
                                                rel="noopener noreferrer" className="rap-btn-icon" title="Ver página pública">
                                                <ExternalLink size={14} />
                                            </a>
                                        )}
                                        <button className="rap-btn-icon" title="Configurar marketing"
                                            onClick={() => { setMktProductId(p._id); setActiveTab('marketing'); }}>
                                            <Globe size={14} />
                                        </button>
                                        <button className="rap-btn-icon" onClick={() => openEdit(p)} title="Editar produto">
                                            <Edit3 size={14} />
                                        </button>
                                        <button
                                            className={`rap-active-toggle${p.active ? ' on' : ''}`}
                                            onClick={() => handleToggleActive(p)}
                                            title={p.active ? 'Desativar' : 'Ativar'}>
                                            {p.active ? 'Ativo' : 'Inativo'}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ══════════════════════════════════════════ */}
            {/* ABA MARKETING                             */}
            {/* ══════════════════════════════════════════ */}
            {activeTab === 'marketing' && (
                <div className="rap-tab-content">
                    <div className="rap-mkt-controls-row">
                        <div className="rap-mkt-controls-left">
                            {mktProductPeriods.length > 0 && (
                                <div className="rap-mkt-period-filter">
                                    <button type="button"
                                        className={`rap-period-chip${mktPeriodFilter === 'all' ? ' active' : ''}`}
                                        onClick={() => setMktPeriodFilter('all')}>
                                        Todos
                                    </button>
                                    {mktProductPeriods.map(period => (
                                        <button key={period} type="button"
                                            className={`rap-period-chip${mktPeriodFilter === period ? ' active' : ''}`}
                                            onClick={() => setMktPeriodFilter(period)}>
                                            {period}° período
                                        </button>
                                    ))}
                                </div>
                            )}
                            <div className="rap-field rap-mkt-selector">
                                <label className="rap-label">Página de Divulgação</label>
                                <RapProductDropdown
                                    products={mktFilteredProducts}
                                    value={mktProductId}
                                    onChange={id => { setMktProductId(id); setMktMode('produto'); }}
                                    placeholder="— selecione um produto —"
                                />
                            </div>
                        </div>
                        <div className="rap-mkt-controls-right">
                            <button type="button"
                                className={`rap-btn-primary${mktMode === 'criar-campanha' ? ' active' : ''}`}
                                onClick={() => { setMktCampaignCreateSignal(s => s + 1); setMktMode('criar-campanha'); }}>
                                <Plus size={14} /> Criar campanha
                            </button>
                            <button type="button"
                                className={`rap-btn-ghost${mktMode === 'ver-campanhas' ? ' active' : ''}`}
                                onClick={() => setMktMode('ver-campanhas')}>
                                <Sparkles size={14} /> Ver campanhas
                            </button>
                        </div>
                    </div>

                    {mktMode !== 'produto' && (
                        <RyanAdminCampaigns
                            products={products}
                            mode={mktMode === 'ver-campanhas' ? 'list' : 'form'}
                            onModeChange={m => setMktMode(m === 'list' ? 'ver-campanhas' : 'criar-campanha')}
                            createSignal={mktCampaignCreateSignal}
                        />
                    )}

                    {mktMode === 'produto' && !mktProductId && (
                        <div className="rap-empty">
                            <Globe size={40} color="#0D3B0E" style={{ opacity: 0.2 }} />
                            <p>Selecione um produto para configurar a página pública.</p>
                        </div>
                    )}

                    {mktMode === 'produto' && mktProductId && (
                        <div className="rap-mkt-form">

                            <Section title="Hero" icon={<FileText size={15} />} variant="solid">
                                <div className="rap-field">
                                    <label className="rap-label">Nome do produto</label>
                                    <p className="rap-hero-name-display">{mktProduct?.title}</p>
                                    <p className="rap-hint">Definido na aba Produtos — não editável aqui.</p>
                                </div>
                                <div className="rap-field">
                                    <label className="rap-label">Descrição hero</label>
                                    <textarea className="rap-textarea" rows={2}
                                        value={mktForm.heroDescription}
                                        onChange={e => setMkt({ heroDescription: e.target.value })}
                                        placeholder="Texto abaixo do título no hero..." />
                                </div>
                                <div className="rap-field">
                                    <label className="rap-label">Fonte do título</label>
                                    <div className="rap-hero-font-picker">
                                        {RYAN_HERO_FONTS.map(font => (
                                            <button key={font.id} type="button"
                                                className={`rap-hero-font-btn${mktForm.heroTitleFont === font.id ? ' active' : ''}`}
                                                style={{ fontFamily: font.family }}
                                                onClick={() => setMkt({ heroTitleFont: font.id })}>
                                                <span className="rap-hero-font-sample">{mktProduct?.title || 'Nome do produto'}</span>
                                                <span className="rap-hero-font-label">{font.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </Section>

                            <Section title="Estatísticas" icon={<BarChart2 size={15} />}>
                                <div className="rap-field rap-field--row">
                                    <label className="rap-label">Mostrar seção</label>
                                    <button type="button" className={`rap-toggle${mktForm.showStats ? ' on' : ''}`}
                                        onClick={() => setMkt({ showStats: !mktForm.showStats })}>
                                        <span className="rap-toggle-knob" />
                                    </button>
                                </div>
                                <div className="rap-field">
                                    <label className="rap-label">Modo</label>
                                    <div className="rap-type-chips">
                                        {(['auto', 'custom'] as const).map(m => (
                                            <button key={m} type="button"
                                                className={`rap-type-chip${mktForm.statsMode === m ? ' active' : ''}`}
                                                onClick={() => setMkt({ statsMode: m })}>
                                                {m === 'auto' ? 'Automático' : 'Personalizado'}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                {mktForm.statsMode === 'custom' && (
                                    <div className="rap-field">
                                        <label className="rap-label">Conteúdo personalizado</label>
                                        <DescBlockEditorInline
                                            value={mktForm.statsCustomContent}
                                            onChange={v => setMkt({ statsCustomContent: v })}
                                        />
                                    </div>
                                )}
                            </Section>

                            <Section title="Bloco Diferencial" icon={<Sparkles size={15} />} variant="solid">
                                <div className="rap-field rap-field--row">
                                    <label className="rap-label">Habilitado</label>
                                    <button type="button" className={`rap-toggle${mktForm.differentialEnabled ? ' on' : ''}`}
                                        onClick={() => setMkt({ differentialEnabled: !mktForm.differentialEnabled })}>
                                        <span className="rap-toggle-knob" />
                                    </button>
                                </div>
                                {mktForm.differentialEnabled && (
                                    <>
                                        <div className="rap-field">
                                            <label className="rap-label">Título</label>
                                            <input className="rap-input" value={mktForm.differentialTitle}
                                                onChange={e => setMkt({ differentialTitle: e.target.value })} />
                                        </div>
                                        <div className="rap-field">
                                            <label className="rap-label">Conteúdo (esquerda)</label>
                                            <DescBlockEditorInline
                                                value={mktForm.differentialContent}
                                                onChange={v => setMkt({ differentialContent: v })}
                                            />
                                        </div>
                                        <div className="rap-field">
                                            <label className="rap-label">Lado direito</label>
                                            <div className="rap-type-chips">
                                                {(['none', 'animation_osce', 'stat_cards'] as const).map(m => (
                                                    <button key={m} type="button"
                                                        className={`rap-type-chip${mktForm.differentialRightMode === m ? ' active' : ''}`}
                                                        onClick={() => setMkt({ differentialRightMode: m })}>
                                                        {m === 'none' ? 'Nenhum' : m === 'animation_osce' ? 'Animação OSCE' : 'Stat Cards'}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        {mktForm.differentialRightMode === 'stat_cards' && (
                                            <div className="rap-stat-cards-grid">
                                                {mktForm.differentialStatCards.map((card, i) => {
                                                    const IC = DESC_ICONS.find(x => x.name === card.icon);
                                                    const IconC = IC?.component ?? BookOpen;
                                                    return (
                                                        <div key={i} className="rap-stat-card-edit">
                                                            <div className="rap-dbe-icon-wrap">
                                                                <button type="button" className="rap-dbe-icon-btn"
                                                                    onClick={() => setStatCardIconPicker(statCardIconPicker === i ? null : i)}>
                                                                    <IconC size={14} />
                                                                </button>
                                                                {statCardIconPicker === i && (
                                                                    <div className="rap-dbe-picker">
                                                                        {DESC_ICONS.map(ic => {
                                                                            const C = ic.component;
                                                                            return (
                                                                                <button key={ic.name} type="button"
                                                                                    className={`rap-dbe-pick ${card.icon === ic.name ? 'active' : ''}`}
                                                                                    onClick={() => { setStatCard(i, { icon: ic.name }); setStatCardIconPicker(null); }}>
                                                                                    <C size={13} />
                                                                                </button>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <input className="rap-input" value={card.value}
                                                                onChange={e => setStatCard(i, { value: e.target.value })}
                                                                placeholder="Valor (ex: 500+)" />
                                                            <input className="rap-input" value={card.label}
                                                                onChange={e => setStatCard(i, { label: e.target.value })}
                                                                placeholder="Label (ex: Flashcards)" />
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        {mktForm.differentialRightMode === 'animation_osce' && (
                                            <div className="rap-field">
                                                <label className="rap-label">Cenário da animação OSCE</label>
                                                <div className="rap-field-row">
                                                    <input className="rap-input" value={mktForm.osceScenario.badge}
                                                        onChange={e => setMkt({ osceScenario: { ...mktForm.osceScenario, badge: e.target.value } })}
                                                        placeholder="Selo (ex: Estação OSCE · Cardiologia)" />
                                                    <input className="rap-input" value={mktForm.osceScenario.examinerLabel}
                                                        onChange={e => setMkt({ osceScenario: { ...mktForm.osceScenario, examinerLabel: e.target.value } })}
                                                        placeholder="Rótulo (ex: O examinador pergunta:)" />
                                                </div>
                                                <textarea className="rap-textarea" rows={2} value={mktForm.osceScenario.question}
                                                    onChange={e => setMkt({ osceScenario: { ...mktForm.osceScenario, question: e.target.value } })}
                                                    placeholder="Pergunta do examinador" />
                                                <input className="rap-input" value={mktForm.osceScenario.hint}
                                                    onChange={e => setMkt({ osceScenario: { ...mktForm.osceScenario, hint: e.target.value } })}
                                                    placeholder="Dica (ex: Responda em voz alta, depois confira:)" />
                                                <input className="rap-input" value={mktForm.osceScenario.answerLabel}
                                                    onChange={e => setMkt({ osceScenario: { ...mktForm.osceScenario, answerLabel: e.target.value } })}
                                                    placeholder="Rótulo da resposta (ex: Descrição do sopro)" />
                                                <textarea className="rap-textarea" rows={3} value={mktForm.osceScenario.answerText}
                                                    onChange={e => setMkt({ osceScenario: { ...mktForm.osceScenario, answerText: e.target.value } })}
                                                    placeholder="Texto da resposta revelada" />
                                            </div>
                                        )}
                                    </>
                                )}
                            </Section>

                            <Section title="Blocos de Demonstração" icon={<Sparkles size={15} />}>
                                <p className="rap-hint">
                                    Demonstrações interativas do produto (grifos, flashcards, quiz "ao vivo"), com conteúdo totalmente editável.
                                    Adicione quantos blocos quiser, em qualquer ordem — eles entram na página logo após o Bloco Diferencial.
                                </p>

                                {mktForm.contentBlocks.length === 0 && (
                                    <p className="rap-hint">Nenhum bloco adicionado ainda.</p>
                                )}

                                <DndContext sensors={blockSensors} collisionDetection={closestCenter} onDragEnd={handleBlockDragEnd}>
                                    <SortableContext
                                        items={[...mktForm.contentBlocks].sort((a, b) => a.order - b.order).map(b => b.id)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        <div className="rap-block-list-outer">
                                            {[...mktForm.contentBlocks].sort((a, b) => a.order - b.order).map(block => (
                                                <SortableBlockItem
                                                    key={block.id}
                                                    block={block}
                                                    expanded={expandedBlockId === block.id}
                                                    onToggleExpand={() => setExpandedBlockId(id => id === block.id ? null : block.id)}
                                                    onToggleEnabled={() => updateBlock(block.id, { enabled: !block.enabled })}
                                                    onUpdateData={patch => updateBlockData(block.id, patch)}
                                                    onUpdateBackgroundVariant={variant => updateBlock(block.id, { backgroundVariant: variant })}
                                                    onRemove={() => removeBlock(block.id)}
                                                    deleteConfirm={blockDeleteConfirm === block.id}
                                                    onRequestDelete={() => setBlockDeleteConfirm(block.id)}
                                                    onCancelDelete={() => setBlockDeleteConfirm(null)}
                                                />
                                            ))}
                                        </div>
                                    </SortableContext>
                                </DndContext>

                                {addBlockPickerOpen ? (
                                    <div className="rap-block-type-picker">
                                        <button type="button" className="rap-block-type-option" onClick={() => addBlock('apostila_demo')}>
                                            <ScrollText size={16} /> Apostila Interativa
                                        </button>
                                        <button type="button" className="rap-block-type-option" onClick={() => addBlock('flashcards_demo')}>
                                            <Zap size={16} /> Flashcards Adaptativos
                                        </button>
                                        <button type="button" className="rap-block-type-option" onClick={() => addBlock('metodo_demo')}>
                                            <Sparkles size={16} /> Método GRAIA
                                        </button>
                                        <button type="button" className="rap-block-type-option" onClick={() => addBlock('estudo_ativo_demo')}>
                                            <GraduationCap size={16} /> Estudo Ativo
                                        </button>
                                        <button type="button" className="rap-block-type-option" onClick={() => addBlock('tutoria_demo')}>
                                            <Users size={16} /> Tutoria
                                        </button>
                                        <button type="button" className="rap-block-type-option" onClick={() => addBlock('video_demo')}>
                                            <Video size={16} /> Vídeo
                                        </button>
                                        <button type="button" className="rap-btn-ghost" onClick={() => setAddBlockPickerOpen(false)}>Cancelar</button>
                                    </div>
                                ) : (
                                    <button type="button" className="rap-dbe-add-li" onClick={() => setAddBlockPickerOpen(true)}>
                                        <Plus size={11} /> Adicionar bloco
                                    </button>
                                )}
                            </Section>

                            <Section title="Conteúdo incluído" icon={<Layers size={15} />} variant="solid">
                                <div className="rap-field-inline-row">
                                    <div className="rap-field">
                                        <label className="rap-label">Organização</label>
                                        <div className="rap-type-chips">
                                            <button type="button"
                                                className={`rap-type-chip${mktForm.contentOrganization === 'tabs' ? ' active' : ''}`}
                                                onClick={() => setMkt({ contentOrganization: 'tabs' })}>
                                                Espelhar abas
                                            </button>
                                            <button type="button"
                                                className={`rap-type-chip${mktForm.contentOrganization === 'categories' ? ' active' : ''}`}
                                                onClick={() => setMkt({ contentOrganization: 'categories' })}>
                                                Espelhar categorias
                                            </button>
                                            <button type="button"
                                                className={`rap-type-chip${mktForm.contentOrganization === 'flat' ? ' active' : ''}`}
                                                onClick={() => setMkt({ contentOrganization: 'flat' })}>
                                                Sempre simples
                                            </button>
                                            <button type="button"
                                                className={`rap-type-chip${mktForm.contentOrganization === 'custom' ? ' active' : ''}`}
                                                onClick={() => setMkt({ contentOrganization: 'custom' })}>
                                                Personalizado
                                            </button>
                                        </div>
                                    </div>
                                    {mktForm.contentOrganization !== 'flat' && (
                                        <label className="rap-check rap-check--inline">
                                            <input type="checkbox" checked={mktForm.contentDropdown}
                                                onChange={e => setMkt({ contentDropdown: e.target.checked })} />
                                            Usar dropdown
                                        </label>
                                    )}
                                    <div className="rap-field">
                                        <label className="rap-label">Colunas do grid</label>
                                        <div className="rap-type-chips">
                                            {[2, 3].map(n => (
                                                <button key={n} type="button"
                                                    className={`rap-type-chip${mktForm.gridCols === n ? ' active' : ''}`}
                                                    onClick={() => setMkt({ gridCols: n })}>
                                                    {n} colunas
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className="rap-field-inline-row">
                                    <label className="rap-check rap-check--inline">
                                        <input type="checkbox" checked={mktForm.showContentGroupCounts}
                                            onChange={e => setMkt({ showContentGroupCounts: e.target.checked })} />
                                        Mostrar quantidade geral (por space/seção)
                                    </label>
                                    <label className="rap-check rap-check--inline">
                                        <input type="checkbox" checked={mktForm.showContentItemCounts}
                                            onChange={e => setMkt({ showContentItemCounts: e.target.checked })} />
                                        Mostrar quantidade em cada item
                                    </label>
                                </div>
                                <p className="rap-hint">A ordem dos decks e apostilas segue automaticamente a ordem do campo na RyanField — para mudar, arraste lá.</p>
                                {loadingContent && <div className="rap-mini-spinner"><div className="rap-spinner" /></div>}
                                {!loadingContent && allSubdecks.length === 0 && (
                                    <p className="rap-hint">Nenhum deck encontrado. O produto precisa ter campos com deck associado.</p>
                                )}
                                {!loadingContent && allSubdecks.length > 0 && mktForm.contentOrganization === 'tabs' && (
                                    <>
                                        {allTabs.length === 0 && (
                                            <p className="rap-hint">Nenhuma aba configurada nos campos deste produto — configure abas em RyanFieldPage, ou escolha outra organização acima.</p>
                                        )}
                                        {[...allTabs, { _id: '__no_tab__', name: 'Sem aba' }].map(tab => {
                                            const items = allSubdecks
                                                .filter(sd => tab._id === '__no_tab__' ? !sd.tabId : sd.tabId === tab._id);
                                            if (items.length === 0) return null;
                                            const iconId = mktForm.tabIcons[tab._id] || null;
                                            const TabIcon = iconId ? getRyanMedicalIcon(iconId) : null;
                                            return (
                                                <SubdeckSectionShell
                                                    key={tab._id}
                                                    icon={TabIcon ? <TabIcon size={13} /> : <Layers size={13} />}
                                                    label={tab.name}
                                                    count={items.length}
                                                    isOpen={expandedSections.has(tab._id)}
                                                    onToggleOpen={() => toggleSectionExpanded(tab._id)}
                                                    headerExtra={tab._id !== '__no_tab__' && (
                                                        <button type="button" className="rap-subdeck-tab-icon-btn"
                                                            onClick={e => { e.stopPropagation(); setTabIconModalId(tab._id); }}>
                                                            {TabIcon ? <TabIcon size={13} /> : <Plus size={11} />}
                                                            {iconId ? 'Trocar ícone' : 'Ícone da aba'}
                                                        </button>
                                                    )}
                                                >
                                                    <DndContext sensors={subdeckSensors} collisionDetection={closestCenter} onDragEnd={handleSubdeckDragEnd(items)}>
                                                        <SortableContext items={items.map(sd => sd._id)} strategy={verticalListSortingStrategy}>
                                                            <div className="rap-subdeck-grid">
                                                                {items.map(sd => (
                                                                    <SortableSubdeckCard
                                                                        key={sd._id}
                                                                        sd={sd}
                                                                        isOpen={expandedSubdecks.has(sd._id)}
                                                                        onToggleOpen={() => toggleSubdeckExpanded(sd._id)}
                                                                        descValue={mktForm.subdeckDescriptions[sd._id] || ''}
                                                                        descPreviewText={descPreview(mktForm.subdeckDescriptions[sd._id] || '')}
                                                                        isDescOpen={expandedDescIds.has(sd._id)}
                                                                        onToggleDesc={() => toggleDescExpanded(sd._id)}
                                                                        onChangeDesc={v => setMkt({ subdeckDescriptions: { ...mktForm.subdeckDescriptions, [sd._id]: v } })}
                                                                        previewLink={mktForm.subdeckPreviewLinks[sd._id] || ''}
                                                                        onEditLink={() => openPreviewLinkModal(sd._id)}
                                                                    />
                                                                ))}
                                                            </div>
                                                        </SortableContext>
                                                    </DndContext>
                                                </SubdeckSectionShell>
                                            );
                                        })}
                                    </>
                                )}
                                {!loadingContent && allSubdecks.length > 0 && mktForm.contentOrganization === 'custom' && (
                                    <>
                                        <div className="rap-custom-section-creator">
                                            <input
                                                className="rap-input"
                                                value={newSectionName}
                                                onChange={e => setNewSectionName(e.target.value)}
                                                onKeyDown={e => e.key === 'Enter' && addCustomSection()}
                                                placeholder='Nome da nova seção (ex: "Gastroenterologia - Patologias")'
                                            />
                                            <button type="button" className="rap-btn-primary-sm" onClick={addCustomSection} disabled={!newSectionName.trim()}>
                                                <Plus size={13} /> Criar seção
                                            </button>
                                        </div>

                                        {mktForm.customSections.length === 0 && (
                                            <p className="rap-hint">Nenhuma seção criada ainda — crie uma acima pra começar a organizar do seu jeito.</p>
                                        )}

                                        {[...mktForm.customSections].sort((a, b) => a.order - b.order).map((section, idx, sorted) => {
                                            const items = allSubdecks
                                                .filter(sd => mktForm.customSectionAssignments[sd._id] === section.id);
                                            const isEditingName = editingSectionId === section.id;
                                            const SectionIcon = section.icon ? getRyanMedicalIcon(section.icon) : null;
                                            return (
                                                <SubdeckSectionShell
                                                    key={section.id}
                                                    icon={SectionIcon ? <SectionIcon size={13} /> : <Layers size={13} />}
                                                    label={section.name}
                                                    count={items.length}
                                                    isOpen={expandedSections.has(section.id)}
                                                    onToggleOpen={() => toggleSectionExpanded(section.id)}
                                                    headerExtra={(
                                                        <div className="rap-custom-section-actions" onClick={e => e.stopPropagation()}>
                                                            <button type="button" className="rap-icon-btn-sm" title="Mover pra cima" disabled={idx === 0}
                                                                onClick={() => moveCustomSection(section.id, -1)}>
                                                                <ChevronUp size={13} />
                                                            </button>
                                                            <button type="button" className="rap-icon-btn-sm" title="Mover pra baixo" disabled={idx === sorted.length - 1}
                                                                onClick={() => moveCustomSection(section.id, 1)}>
                                                                <ChevronDown size={13} />
                                                            </button>
                                                            <button type="button" className="rap-icon-btn-sm" title={section.icon ? 'Trocar ícone' : 'Ícone da seção'}
                                                                onClick={() => setSectionIconModalId(section.id)}>
                                                                {SectionIcon ? <SectionIcon size={13} /> : <Plus size={11} />}
                                                            </button>
                                                            <button type="button" className="rap-icon-btn-sm" title="Renomear"
                                                                onClick={() => setEditingSectionId(isEditingName ? null : section.id)}>
                                                                <Edit3 size={13} />
                                                            </button>
                                                            <button type="button" className="rap-icon-btn-sm rap-icon-btn-sm--danger" title="Excluir seção"
                                                                onClick={() => {
                                                                    if (window.confirm(`Excluir a seção "${section.name}"? Os itens dela voltam pra "Outros materiais".`)) deleteCustomSection(section.id);
                                                                }}>
                                                                <Trash2 size={13} />
                                                            </button>
                                                        </div>
                                                    )}
                                                >
                                                    {isEditingName && (
                                                        <div className="rap-field" onClick={e => e.stopPropagation()}>
                                                            <input
                                                                className="rap-input"
                                                                value={section.name}
                                                                autoFocus
                                                                onChange={e => renameCustomSection(section.id, e.target.value)}
                                                                onKeyDown={e => e.key === 'Enter' && setEditingSectionId(null)}
                                                                onBlur={() => setEditingSectionId(null)}
                                                            />
                                                        </div>
                                                    )}
                                                    <div className="rap-field" onClick={e => e.stopPropagation()}>
                                                        <label className="rap-label">Descrição da seção (opcional)</label>
                                                        <textarea
                                                            className="rap-textarea"
                                                            rows={2}
                                                            value={section.description}
                                                            onChange={e => setCustomSectionDescription(section.id, e.target.value)}
                                                            placeholder='Ex: "Conteúdos de HAM e IESC com foco em abordagem clínica, comunicação, neonatologia, pediatria e atenção à saúde."'
                                                        />
                                                    </div>
                                                    {items.length === 0 ? (
                                                        <p className="rap-hint">Nenhum item nesta seção ainda — escolha essa seção no seletor de cada card abaixo.</p>
                                                    ) : (
                                                        <DndContext sensors={subdeckSensors} collisionDetection={closestCenter} onDragEnd={handleSubdeckDragEnd(items)}>
                                                            <SortableContext items={items.map(sd => sd._id)} strategy={verticalListSortingStrategy}>
                                                                <div className="rap-subdeck-grid">
                                                                    {items.map(sd => (
                                                                        <SortableSubdeckCard
                                                                            key={sd._id}
                                                                            sd={sd}
                                                                            isOpen={expandedSubdecks.has(sd._id)}
                                                                            onToggleOpen={() => toggleSubdeckExpanded(sd._id)}
                                                                            descValue={mktForm.subdeckDescriptions[sd._id] || ''}
                                                                            descPreviewText={descPreview(mktForm.subdeckDescriptions[sd._id] || '')}
                                                                            isDescOpen={expandedDescIds.has(sd._id)}
                                                                            onToggleDesc={() => toggleDescExpanded(sd._id)}
                                                                            onChangeDesc={v => setMkt({ subdeckDescriptions: { ...mktForm.subdeckDescriptions, [sd._id]: v } })}
                                                                            previewLink={mktForm.subdeckPreviewLinks[sd._id] || ''}
                                                                            onEditLink={() => openPreviewLinkModal(sd._id)}
                                                                            headerExtra={(
                                                                                <select
                                                                                    className="rap-custom-section-select"
                                                                                    value={section.id}
                                                                                    onChange={e => assignSubdeckToSection(sd._id, e.target.value)}
                                                                                >
                                                                                    <option value="">Sem seção</option>
                                                                                    {mktForm.customSections.map(s => (
                                                                                        <option key={s.id} value={s.id}>{s.name}</option>
                                                                                    ))}
                                                                                </select>
                                                                            )}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            </SortableContext>
                                                        </DndContext>
                                                    )}
                                                </SubdeckSectionShell>
                                            );
                                        })}

                                        {(() => {
                                            const validSectionIds = new Set(mktForm.customSections.map(s => s.id));
                                            const unassigned = allSubdecks
                                                .filter(sd => !validSectionIds.has(mktForm.customSectionAssignments[sd._id] || ''));
                                            if (unassigned.length === 0) return null;
                                            return (
                                                <SubdeckSectionShell
                                                    icon={<Layers size={13} />}
                                                    label="Outros materiais (sem seção)"
                                                    count={unassigned.length}
                                                    isOpen={expandedSections.has('__unassigned__')}
                                                    onToggleOpen={() => toggleSectionExpanded('__unassigned__')}
                                                >
                                                    <div className="rap-subdeck-grid">
                                                        {unassigned.map(sd => (
                                                            <SortableSubdeckCard
                                                                key={sd._id}
                                                                sd={sd}
                                                                isOpen={expandedSubdecks.has(sd._id)}
                                                                onToggleOpen={() => toggleSubdeckExpanded(sd._id)}
                                                                descValue={mktForm.subdeckDescriptions[sd._id] || ''}
                                                                descPreviewText={descPreview(mktForm.subdeckDescriptions[sd._id] || '')}
                                                                isDescOpen={expandedDescIds.has(sd._id)}
                                                                onToggleDesc={() => toggleDescExpanded(sd._id)}
                                                                onChangeDesc={v => setMkt({ subdeckDescriptions: { ...mktForm.subdeckDescriptions, [sd._id]: v } })}
                                                                previewLink={mktForm.subdeckPreviewLinks[sd._id] || ''}
                                                                onEditLink={() => openPreviewLinkModal(sd._id)}
                                                                headerExtra={mktForm.customSections.length > 0 ? (
                                                                    <select
                                                                        className="rap-custom-section-select"
                                                                        value=""
                                                                        onChange={e => assignSubdeckToSection(sd._id, e.target.value)}
                                                                    >
                                                                        <option value="">Sem seção</option>
                                                                        {mktForm.customSections.map(s => (
                                                                            <option key={s.id} value={s.id}>{s.name}</option>
                                                                        ))}
                                                                    </select>
                                                                ) : undefined}
                                                            />
                                                        ))}
                                                    </div>
                                                </SubdeckSectionShell>
                                            );
                                        })()}
                                    </>
                                )}
                                {!loadingContent && allSubdecks.length > 0 && mktForm.contentOrganization !== 'tabs' && mktForm.contentOrganization !== 'custom' && (
                                    <>
                                        {([
                                            { key: 'apostila' as const, label: 'Apostilas', Icon: ScrollText },
                                            { key: 'flash' as const, label: 'Flashcards', Icon: Brain },
                                        ]).map(group => {
                                            const items = allSubdecks
                                                .filter(sd => group.key === 'apostila' ? sd.type === 'apostila' : sd.type !== 'apostila');
                                            if (items.length === 0) return null;
                                            const GroupIcon = group.Icon;
                                            return (
                                                <SubdeckSectionShell
                                                    key={group.key}
                                                    icon={<GroupIcon size={13} />}
                                                    label={group.label}
                                                    count={items.length}
                                                    isOpen={expandedSections.has(group.key)}
                                                    onToggleOpen={() => toggleSectionExpanded(group.key)}
                                                >
                                                    <DndContext sensors={subdeckSensors} collisionDetection={closestCenter} onDragEnd={handleSubdeckDragEnd(items)}>
                                                        <SortableContext items={items.map(sd => sd._id)} strategy={verticalListSortingStrategy}>
                                                            <div className="rap-subdeck-grid">
                                                                {items.map(sd => (
                                                                    <SortableSubdeckCard
                                                                        key={sd._id}
                                                                        sd={sd}
                                                                        isOpen={expandedSubdecks.has(sd._id)}
                                                                        onToggleOpen={() => toggleSubdeckExpanded(sd._id)}
                                                                        descValue={mktForm.subdeckDescriptions[sd._id] || ''}
                                                                        descPreviewText={descPreview(mktForm.subdeckDescriptions[sd._id] || '')}
                                                                        isDescOpen={expandedDescIds.has(sd._id)}
                                                                        onToggleDesc={() => toggleDescExpanded(sd._id)}
                                                                        onChangeDesc={v => setMkt({ subdeckDescriptions: { ...mktForm.subdeckDescriptions, [sd._id]: v } })}
                                                                        previewLink={mktForm.subdeckPreviewLinks[sd._id] || ''}
                                                                        onEditLink={() => openPreviewLinkModal(sd._id)}
                                                                    />
                                                                ))}
                                                            </div>
                                                        </SortableContext>
                                                    </DndContext>
                                                </SubdeckSectionShell>
                                            );
                                        })}
                                    </>
                                )}
                            </Section>

                            <Section title="Adquira Agora" icon={<CreditCard size={15} />}>
                                <div className="rap-field-row">
                                    <label className="rap-check">
                                        <input type="checkbox" checked={mktForm.showPriceHero}
                                            onChange={e => setMkt({ showPriceHero: e.target.checked })} />
                                        Mostrar preço no Hero
                                    </label>
                                    <label className="rap-check">
                                        <input type="checkbox" checked={mktForm.showPriceClose}
                                            onChange={e => setMkt({ showPriceClose: e.target.checked })} />
                                        Mostrar preço no Adquira agora
                                    </label>
                                </div>
                                <div className="rap-field">
                                    <label className="rap-label rap-label--with-tip">
                                        Chave PIX
                                        <RapInfoTip text={`O fluxo padrão de pagamento hoje é automático (Pix ou cartão via Mercado Pago, direto no checkout) — o QR Code que o aluno vê ali é gerado na hora pelo Mercado Pago, não usa os campos de chave PIX abaixo. Chave PIX/titular/cidade ficam só como fallback manual/legado, sem uso no botão principal de compra. O campo WhatsApp continua em uso normal: é o número mostrado no "Dúvidas? Fale conosco" da página pública.`} />
                                    </label>
                                    <input className="rap-input" value={mktForm.pixKey}
                                        onChange={e => setMkt({ pixKey: e.target.value })}
                                        placeholder="CPF, CNPJ, email ou telefone..." />
                                </div>
                                <div className="rap-field-row">
                                    <div className="rap-field">
                                        <label className="rap-label">Titular PIX</label>
                                        <input className="rap-input" value={mktForm.pixHolderName}
                                            onChange={e => setMkt({ pixHolderName: e.target.value })} />
                                    </div>
                                    <div className="rap-field">
                                        <label className="rap-label rap-label--with-tip">
                                            Cidade do titular
                                            <RapInfoTip text="Titular e cidade só valem pro fallback manual/legado (sem acentos, é o padrão exigido pelos apps de banco) — o checkout automático gera o próprio QR Code pelo Mercado Pago e não usa estes campos." />
                                        </label>
                                        <input className="rap-input" value={mktForm.pixCity}
                                            onChange={e => setMkt({ pixCity: e.target.value.toUpperCase() })}
                                            placeholder="SAO PAULO" />
                                    </div>
                                </div>
                                <div className="rap-field">
                                    <label className="rap-label">WhatsApp de dúvidas</label>
                                    <input className="rap-input" value={mktForm.whatsappNumber}
                                        onChange={e => setMkt({ whatsappNumber: e.target.value })}
                                        placeholder="55119..." />
                                </div>
                            </Section>

                            <Section title="Checkout e Upsell" icon={<Sparkles size={15} />} variant="solid">
                                <div className="rap-type-chips">
                                    <button type="button" className={`rap-type-chip${mktForm.checkoutShowFlashcards ? ' active' : ''}`}
                                        onClick={() => setMkt({ checkoutShowFlashcards: !mktForm.checkoutShowFlashcards })}>
                                        Quantidade de flashcards
                                    </button>
                                    <button type="button" className={`rap-type-chip${mktForm.checkoutShowApostilas ? ' active' : ''}`}
                                        onClick={() => setMkt({ checkoutShowApostilas: !mktForm.checkoutShowApostilas })}>
                                        Quantidade de apostilas
                                    </button>
                                    <button type="button" className={`rap-type-chip${mktForm.checkoutShowDecks ? ' active' : ''}`}
                                        onClick={() => setMkt({ checkoutShowDecks: !mktForm.checkoutShowDecks })}>
                                        Quantidade de decks
                                    </button>
                                </div>

                                <div className="rap-upsell-divider rap-upsell-divider--tight" />

                                <RyanAdminProductUpsell
                                    productId={mktProductId}
                                    productTitle={mktProduct?.title || ''}
                                    productPriceCents={mktProduct?.priceCents || 0}
                                />
                            </Section>

                            <div className="rap-closing-block">
                                <div className="rap-closing-block-header">
                                    <h3>Publicação</h3>
                                    <div className="rap-closing-block-actions">
                                        {mktProduct?.publicPage?.enabled && mktProduct?.publicSlug && (
                                            <a href={`/ryan/produto/${mktProduct.publicSlug}`} target="_blank"
                                                rel="noopener noreferrer" className="rap-btn-ghost rap-btn-ghost--on-dark">
                                                <Eye size={14} /> Ver página
                                            </a>
                                        )}
                                        <button className="rap-btn-primary rap-btn-primary--on-dark" onClick={handleSaveMarketing} disabled={savingMkt}>
                                            {mktSaved
                                                ? <><CheckCircle size={14} /> Salvo!</>
                                                : savingMkt ? 'Salvando…'
                                                : <><Save size={14} /> Salvar</>}
                                        </button>
                                    </div>
                                </div>
                                <div className="rap-field rap-field--row">
                                    <label className="rap-label">Página habilitada</label>
                                    <button type="button" className={`rap-toggle${mktForm.enabled ? ' on' : ''}`}
                                        onClick={() => setMkt({ enabled: !mktForm.enabled })}>
                                        <span className="rap-toggle-knob" />
                                    </button>
                                </div>
                                <div className="rap-field">
                                    <label className="rap-label">Slug (URL da página)</label>
                                    <input className="rap-input" value={mktSlug}
                                        onChange={e => setMktSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                                        placeholder="ex: apostila-medcurso-5ano" />
                                    {mktSlug && (
                                        <div className="rap-slug-preview">/ryan/produto/{mktSlug}</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

        </div>

        {/* ── Edit Modal (portal) ── */}
        {editOpen && editingId && ReactDOM.createPortal(
            <div className="rap-modal-overlay" onClick={closeEdit}>
                <div className="rap-modal" onClick={e => e.stopPropagation()}>
                    <div className="rap-modal-header">
                        <h3 className="rap-modal-title">Editar produto</h3>
                        <button className="rap-form-close" onClick={closeEdit}><X size={18} /></button>
                    </div>

                    <div className="rap-modal-body">
                        <div className="rap-field">
                            <label className="rap-label">Título</label>
                            <input className="rap-input" value={editForm.title}
                                onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                                placeholder="Nome do produto..." />
                        </div>

                        <div className="rap-field">
                            <label className="rap-label">Conteúdo do produto</label>
                            <div className="rap-grant-trigger-row">
                                <button type="button" className="rap-btn-ghost" onClick={() => setGrantModalTarget('edit')}>
                                    <Layers size={15} /> Selecionar conteúdo
                                </button>
                                <span className="rap-grant-summary-inline">{summarizeGrants(editForm.grants)}</span>
                            </div>
                        </div>

                        <div className="rap-field">
                            <label className="rap-label">Preço (centavos)</label>
                            <input className="rap-input rap-input-price" type="number" min={0}
                                value={editForm.priceCents}
                                onChange={e => setEditForm(f => ({ ...f, priceCents: Number(e.target.value) }))}
                                placeholder="ex: 19000 = R$190,00" />
                            {editForm.priceCents > 0 && (
                                <span className="rap-price-preview">{fmt(editForm.priceCents)}</span>
                            )}
                        </div>

                        <div className="rap-field">
                            <label className="rap-label">Tags de instituição (opcional)</label>
                            <input className="rap-input" type="text" list="rap-institution-tags"
                                value={editForm.institutionTags}
                                onChange={e => setEditForm(f => ({ ...f, institutionTags: e.target.value }))}
                                placeholder="ex: Afya" />
                            <p className="rap-hint">
                                Concedida à conta na compra (soma a institutionTags, nunca substitui) — controla
                                só se um grupo marcado com essa tag aparece na home do comprador, não é acesso a
                                conteúdo.
                            </p>
                        </div>

                        <div className="rap-field">
                            <label className="rap-label">Expiração do acesso (opcional)</label>
                            <input className="rap-input" type="date"
                                value={editForm.accessExpiresAt}
                                onChange={e => setEditForm(f => ({ ...f, accessExpiresAt: e.target.value }))} />
                            <p className="rap-hint">
                                Aplicada pelo webhook do Mercado Pago a todo mundo que comprar este produto a
                                partir de agora — a mesma data pra todos, independente de quando compraram.
                                Vazio = sem expiração (acesso permanente, comportamento atual). Não afeta quem
                                já tem acesso concedido antes desta configuração.
                            </p>

                            {editForm.accessExpiresAt && (
                                <div className="rap-apply-expiry">
                                    {!applyExpiryConfirm ? (
                                        <button type="button" className="rap-btn-ghost"
                                            onClick={() => { setApplyExpiryConfirm(true); setApplyExpiryResult(null); }}>
                                            Aplicar esta data também a quem já comprou
                                        </button>
                                    ) : (
                                        <div className="rap-apply-expiry-confirm">
                                            <span>
                                                Isso vai definir {editForm.accessExpiresAt} como expiração pra todo
                                                mundo que já comprou este produto e HOJE não tem nenhuma data
                                                própria definida. Quem já tem uma data diferente (concedida
                                                manualmente, por exemplo) não é alterado. Confirmar?
                                            </span>
                                            <div className="rap-apply-expiry-confirm-actions">
                                                <button type="button" className="rap-btn-primary"
                                                    onClick={handleApplyExpiryToExisting} disabled={applyingExpiry}>
                                                    {applyingExpiry ? 'Aplicando…' : 'Sim, aplicar'}
                                                </button>
                                                <button type="button" className="rap-btn-ghost"
                                                    onClick={() => setApplyExpiryConfirm(false)} disabled={applyingExpiry}>
                                                    Cancelar
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    {applyExpiryResult && <p className="rap-hint">{applyExpiryResult}</p>}
                                </div>
                            )}
                        </div>

                        <div className="rap-field rap-field--row">
                            <label className="rap-label">Ativo</label>
                            <button type="button"
                                className={`rap-toggle${editForm.active ? ' on' : ''}`}
                                onClick={() => setEditForm(f => ({ ...f, active: !f.active }))}>
                                <span className="rap-toggle-knob" />
                            </button>
                        </div>
                    </div>

                    <div className="rap-modal-footer">
                        <div className="rap-modal-footer-left">
                            {deleteConfirm ? (
                                <div className="rap-modal-delete-confirm">
                                    <span className="rap-modal-delete-text">Excluir permanentemente?</span>
                                    <button className="rap-confirm-yes"
                                        onClick={async () => { await handleDelete(editingId); closeEdit(); }}
                                        disabled={deleting}>
                                        {deleting ? '…' : 'Excluir'}
                                    </button>
                                    <button className="rap-confirm-no" onClick={() => setDeleteConfirm(false)}>
                                        Cancelar
                                    </button>
                                </div>
                            ) : (
                                <button className="rap-btn-modal-delete"
                                    onClick={() => setDeleteConfirm(true)}>
                                    <Trash2 size={13} /> Excluir produto
                                </button>
                            )}
                        </div>
                        <div className="rap-modal-footer-right">
                            <button className="rap-btn-ghost" onClick={closeEdit}>Cancelar</button>
                            <button className="rap-btn-primary" onClick={handleEdit}
                                disabled={saving || !editForm.title.trim()}>
                                {saving ? 'Salvando…' : <><Save size={14} /> Salvar</>}
                            </button>
                        </div>
                    </div>
                </div>
            </div>,
            document.body
        )}

        {previewLinkModalId && ReactDOM.createPortal(
            <div className="rap-modal-overlay" onClick={() => setPreviewLinkModalId(null)}>
                <div className="rap-modal rap-modal--sm" onClick={e => e.stopPropagation()}>
                    <div className="rap-modal-header">
                        <h3 className="rap-modal-title">Link de prévia</h3>
                        <button className="rap-form-close" onClick={() => setPreviewLinkModalId(null)}><X size={18} /></button>
                    </div>
                    <div className="rap-modal-body">
                        <div className="rap-field">
                            <label className="rap-label">URL</label>
                            <input className="rap-input" autoFocus value={previewLinkDraft}
                                onChange={e => setPreviewLinkDraft(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && savePreviewLinkModal()}
                                placeholder="https://..." />
                        </div>
                    </div>
                    <div className="rap-modal-footer">
                        <div className="rap-modal-footer-right">
                            {previewLinkDraft && (
                                <button className="rap-btn-ghost" onClick={() => setPreviewLinkDraft('')}>Remover</button>
                            )}
                            <button className="rap-btn-ghost" onClick={() => setPreviewLinkModalId(null)}>Cancelar</button>
                            <button className="rap-btn-primary" onClick={savePreviewLinkModal}>
                                <Save size={14} /> Salvar
                            </button>
                        </div>
                    </div>
                </div>
            </div>,
            document.body
        )}

        {tabIconModalId && ReactDOM.createPortal(
            <div className="rap-modal-overlay" onClick={() => setTabIconModalId(null)}>
                <div className="rap-modal rap-modal--sm" onClick={e => e.stopPropagation()}>
                    <div className="rap-modal-header">
                        <h3 className="rap-modal-title">Ícone da aba</h3>
                        <button className="rap-form-close" onClick={() => setTabIconModalId(null)}><X size={18} /></button>
                    </div>
                    <div className="rap-modal-body">
                        <p className="rap-hint">Opcional — some da página do produto se você não escolher nenhum.</p>
                        <RyanSubdeckIconPicker
                            value={mktForm.tabIcons[tabIconModalId] || null}
                            onChange={iconId => setMkt({
                                tabIcons: iconId
                                    ? { ...mktForm.tabIcons, [tabIconModalId]: iconId }
                                    : Object.fromEntries(Object.entries(mktForm.tabIcons).filter(([k]) => k !== tabIconModalId)),
                            })}
                        />
                    </div>
                    <div className="rap-modal-footer">
                        <div className="rap-modal-footer-right">
                            <button className="rap-btn-primary" onClick={() => setTabIconModalId(null)}>Concluído</button>
                        </div>
                    </div>
                </div>
            </div>,
            document.body
        )}

        {sectionIconModalId && ReactDOM.createPortal(
            <div className="rap-modal-overlay" onClick={() => setSectionIconModalId(null)}>
                <div className="rap-modal rap-modal--sm" onClick={e => e.stopPropagation()}>
                    <div className="rap-modal-header">
                        <h3 className="rap-modal-title">Ícone da seção</h3>
                        <button className="rap-form-close" onClick={() => setSectionIconModalId(null)}><X size={18} /></button>
                    </div>
                    <div className="rap-modal-body">
                        <p className="rap-hint">Opcional — some da página do produto se você não escolher nenhum.</p>
                        <RyanSubdeckIconPicker
                            value={mktForm.customSections.find(s => s.id === sectionIconModalId)?.icon || null}
                            onChange={iconId => setMkt({
                                customSections: mktForm.customSections.map(s =>
                                    s.id === sectionIconModalId ? { ...s, icon: iconId } : s
                                ),
                            })}
                        />
                    </div>
                    <div className="rap-modal-footer">
                        <div className="rap-modal-footer-right">
                            <button className="rap-btn-primary" onClick={() => setSectionIconModalId(null)}>Concluído</button>
                        </div>
                    </div>
                </div>
            </div>,
            document.body
        )}

        {grantModalTarget === 'create' && (
            <RyanProductGrantModal
                periodKeys={periodKeys}
                groupedSpaces={groupedSpaces}
                groupLabel={groupLabel}
                grants={createForm.grants}
                onChange={grants => setCreateForm(f => ({ ...f, grants }))}
                onClose={() => setGrantModalTarget(null)}
            />
        )}
        {grantModalTarget === 'edit' && (
            <RyanProductGrantModal
                periodKeys={periodKeys}
                groupedSpaces={groupedSpaces}
                groupLabel={groupLabel}
                grants={editForm.grants}
                onChange={grants => setEditForm(f => ({ ...f, grants }))}
                onClose={() => setGrantModalTarget(null)}
            />
        )}
        </>
    );
};

export default RyanAdminProducts;
